// Blueprint (DEC-008, Amir 23 Oct), the design language the product moves to: ink on a sheet, sections
// divided by rules (never outlined), status drawn rather than coloured in, red only for a problem, and
// each technician in their own pen. Every value is the Blueprint sample's, as the full prototype's
// tokens.css took it (pages/ux-full: tokens.css, DESIGN.md); packages/ui-web builds on these names.
//
// Colour names are prefixed bp- (--bp-ink, --bp-pen-3 …) so Blueprint's CSS can sit beside
// Riverstone's while screens move over. The product roles (bg, text, primary …) map onto them.
// Blueprint's primary is its ink, not the company colour: the sample has no brand fill. A company's
// customer pages (design.portal) keep its own colour as their accent, --bp-brand: on staff screens
// that is ink too, so nothing there changes with the company colour.

import { mix } from '../color.ts';
import type { Brand, TokenSet } from '../schema.ts';
import { OFFICE_COMPONENTS, officeMeaning } from './office.ts';

/** The ink Blueprint's primary button is drawn in; a company's colour doesn't change it. */
export const BLUEPRINT_DEFAULT_BRAND: Brand = { primary: '#10324F' };

/** Blueprint colours that exist in both modes, as palette steps (light.ink, dark.ink). */
const MODE_TOKENS = [
  'bg',
  'surface',
  'ink',
  'muted',
  'rule',
  'stroke',
  'grid',
  'signal',
  'on-signal',
  'on-ink',
  'pen-1',
  'pen-2',
  'pen-3',
  'pen-4',
  'pen-5',
  'pen-6',
  'pen-7',
  'pen-8',
  'on-pen',
  'wash-1',
  'wash-2',
  'wash-3',
  'wash-4',
  'wash-5',
  'wash-6',
  'wash-7',
  'wash-8',
  // calls (DESIGN.md §9): a call asking for you now, full; your own line, its quiet wash
  'interrupt',
  'on-interrupt',
  'interrupt-wash',
] as const;

/** How much of a pen tints its sheet in a booked visit's wash (Amir, 26 Oct: option D). */
const WASH = { light: 0.14, dark: 0.18 } as const;

/** The set with 'wash-1' … '-8' added to each mode's palette: each pen mixed into that mode's sheet. */
function withWashes(set: TokenSet): TokenSet {
  const washes = (p: Record<string, string>, weight: number) =>
    Object.fromEntries(
      [1, 2, 3, 4, 5, 6, 7, 8].map((n) => [`wash-${n}`, mix(p[`pen-${n}`]!, p['surface']!, weight)]),
    );
  const { light, dark } = set.palette;
  return {
    ...set,
    palette: {
      ...set.palette,
      light: { ...light, ...washes(light!, WASH.light) },
      dark: { ...dark, ...washes(dark!, WASH.dark) },
    },
  };
}

function designRoles(mode: 'light' | 'dark'): Record<string, string | { customer: string; staff: string }> {
  return {
    ...Object.fromEntries(MODE_TOKENS.map((t) => [`bp-${t}`, `${mode}.${t}`])),
    'bp-scrim': mode === 'light' ? 'light.ink/0.35' : 'fixed.black/0.55',
    // Layers (DESIGN.md §1, Amir 25 Oct: a panel reads as a separate layer): a hard-edged cast with no
    // blur beside a pop-over, a panel or a dialog, and a veil over what a panel sits on. The cast is the
    // rule's colour in both modes: a black one (the audit's first proposal) didn't show on the night sheet.
    'bp-cast': `${mode}.rule`,
    'bp-veil': mode === 'light' ? 'light.bg/0.6' : 'fixed.black/0.4',
    // the customer pages' accent: the rule under the letterhead, the mark, the one filled button, the
    // stars (pages/ux-full DESIGN.md §7)
    'bp-brand': { customer: 'brand.primary', staff: `${mode}.ink` },
    'bp-on-brand': { customer: 'brand.onPrimary', staff: `${mode}.on-ink` },
    // the office's shared stylesheets (office.ts): initials on a person's colour, white in both modes
    'on-avatar': 'fixed.white',
  };
}

function productRoles(mode: 'light' | 'dark') {
  return {
    bg: `${mode}.bg`,
    surface: `${mode}.surface`,
    surfaceRaised: `${mode}.surface`,
    surfaceSunken: `${mode}.bg`,
    text: `${mode}.ink`,
    textMuted: `${mode}.muted`,
    border: `${mode}.rule`,
    borderStrong: `${mode}.ink`,
    focusRing: `${mode}.pen-1`,
    link: `${mode}.ink`,
    // The primary button is ink on the sheet, its label the sheet's colour (the sample's filled button).
    primary: `${mode}.ink`,
    primaryHover: `${mode}.ink`,
    primaryPressed: `${mode}.ink`,
    onPrimary: `${mode}.on-ink`,
    accent: `${mode}.pen-1`,
    onAccent: `${mode}.on-pen`,
    // Blueprint has no green or amber (DESIGN.md §2): success and warning are ink, a problem is red.
    success: `${mode}.ink`,
    onSuccess: `${mode}.on-ink`,
    warning: `${mode}.ink`,
    onWarning: `${mode}.on-ink`,
    danger: `${mode}.signal`,
    onDanger: `${mode}.on-signal`,
    info: `${mode}.pen-1`,
    onInfo: `${mode}.on-pen`,
    overlay: mode === 'light' ? 'light.ink/0.35' : 'fixed.black/0.55',
  };
}

/** A component's eight pens, '<prefix>-pen-1' … '-8', each pointing at a technician's pen colour
 *  (or, with 'wash', '<prefix>-wash-N' at that pen's wash). */
const pens = (prefix: string, part: 'pen' | 'wash' = 'pen'): Record<string, string> =>
  Object.fromEntries(
    [1, 2, 3, 4, 5, 6, 7, 8].map((n) => [`${prefix}-${part}-${n}`, `{color.bp-${part}-${n}}`]),
  );

const type = (size: number, weight = 400, lineHeight = 1.5) => ({
  family: 'body' as const,
  size,
  lineHeight,
  weight,
  tabularNums: true,
});

export const blueprint: TokenSet = withWashes({
  name: 'blueprint',
  version: '1.0.0',
  palette: {
    light: {
      bg: '#F5F8FB',
      surface: '#FFFFFF',
      ink: '#10324F',
      muted: '#4D6A85',
      rule: '#C5D3E0',
      // the ruled lines: the ink on the light sheet
      stroke: '#10324F',
      grid: '#E6EDF4',
      signal: '#D33A2C',
      'on-signal': '#FFFFFF',
      'on-ink': '#FFFFFF',
      'pen-1': '#1F5FA8',
      'pen-2': '#0E7C74',
      'pen-3': '#6A4BB0',
      'pen-4': '#8A6200',
      'pen-5': '#3E7A2E',
      // umber, not brick: a red pen read like the signal red (phase B, 26 Oct)
      'pen-6': '#6B4F3A',
      'pen-7': '#2E6F8E',
      'pen-8': '#8A3B72',
      'on-pen': '#FFFFFF',
      // the interrupt (Amir, 26 Oct; DESIGN.md §9): not red (a problem), not the ink fill, never a pen
      interrupt: '#FFC629',
      'on-interrupt': '#10324F',
      'interrupt-wash': '#FFF4D6',
    },
    dark: {
      bg: '#0A1826',
      surface: '#0F2133',
      ink: '#DCE8F3',
      muted: '#8FA9C1',
      rule: '#2B4560',
      // on the dark sheet the ink would glow, so lines are drawn a step down from it (Amir, 24 Oct)
      stroke: '#5B7A98',
      grid: '#152C43',
      signal: '#FF6B5B',
      'on-signal': '#0A1826',
      'on-ink': '#0A1826',
      'pen-1': '#74AEEF',
      'pen-2': '#4FC7BA',
      'pen-3': '#B19AF2',
      'pen-4': '#E2B54C',
      'pen-5': '#8FCB70',
      'pen-6': '#C8A487',
      'pen-7': '#79C2E0',
      'pen-8': '#E592CD',
      'on-pen': '#0A1826',
      interrupt: '#FFE45C',
      'on-interrupt': '#0A1826',
      'interrupt-wash': '#2A3429',
    },
    fixed: { white: '#FFFFFF', black: '#000000' },
  },
  onColorCandidates: ['fixed.white', 'light.ink'],
  color: {
    light: { ...productRoles('light'), ...designRoles('light') },
    dark: { ...productRoles('dark'), ...designRoles('dark') },
  },
  contrast: [
    { fg: 'bp-muted', bg: 'bp-bg', min: 4.5, why: 'labels and secondary text on the desk' },
    { fg: 'bp-signal', bg: 'bp-surface', min: 4.5, why: 'a problem in words on the sheet' },
    { fg: 'bp-ink', bg: 'bp-grid', min: 4.5, why: 'words in an area waiting on you (wait-area-bg)' },
    { fg: 'bp-muted', bg: 'bp-grid', min: 4.5, why: 'secondary words in an area waiting on you' },
    { fg: 'bp-on-ink', bg: 'bp-ink', min: 4.5, why: 'the primary button and a visit under way' },
    { fg: 'bp-rule', bg: 'bp-surface', min: 1.3, why: 'hairlines stay visible on the sheet' },
    { fg: 'bp-stroke', bg: 'bp-surface', min: 3, why: 'ruled lines divide the sheet' },
    { fg: 'bp-brand', bg: 'bp-surface', min: 3, why: 'the company colour’s rule and stars on the sheet' },
    { fg: 'bp-on-brand', bg: 'bp-brand', min: 4.5, why: 'the label on the customer pages’ filled button' },
    { fg: 'bp-on-interrupt', bg: 'bp-interrupt', min: 4.5, why: 'a ringing call’s words (DESIGN.md §9)' },
    { fg: 'bp-ink', bg: 'bp-interrupt-wash', min: 4.5, why: 'your line: on a call, on hold, taking calls' },
    { fg: 'bp-muted', bg: 'bp-interrupt-wash', min: 4.5, why: 'the secondary words on your line' },
    ...[1, 2, 3, 4, 5, 6, 7, 8].flatMap((n) => [
      { fg: `bp-pen-${n}`, bg: 'bp-surface', min: 3, why: `technician ${n}'s pen line on the sheet` },
      { fg: `bp-on-pen`, bg: `bp-pen-${n}`, min: 4.5, why: `text on technician ${n}'s visit under way` },
      { fg: 'bp-ink', bg: `bp-wash-${n}`, min: 4.5, why: `text on technician ${n}'s booked visit` },
    ]),
  ],
  brandDerive: { light: { hover: 0, pressed: 0 }, dark: { hover: 0, pressed: 0 } },
  typography: {
    families: {
      display: '"Archivo", system-ui, sans-serif',
      body: '"Archivo", system-ui, sans-serif',
      mono: 'ui-monospace, "SF Mono", Menlo, monospace',
    },
    brandFontSlots: [],
    allowedBrandFonts: [],
    // The sample's scale, by what each size is for (DESIGN.md §1). The required roles repeat their twins.
    scale: {
      h1: type(28, 600, 1.1), // the page's title
      figureLg: type(30, 600, 1.1), // a price the customer chooses on
      figure: type(26, 500, 1.1), // a figure in a title block
      h2: type(22, 600, 1.1), // a board's or section's title
      h3: type(19, 600, 1.1), // a card's or option's title
      name: type(17, 600, 1.25), // a person or customer at the top of a section
      bodyLg: type(15, 400, 1.5), // a row's name, a lede
      body: type(14, 400, 1.5), // body, rows, buttons
      table: type(13.5, 400, 1.4), // tables, the side rail
      bodySm: type(13, 400, 1.4), // secondary lines under a name
      label: type(12.5, 600, 1.4), // labels, small buttons
      caption: type(12, 400, 1.35), // the board's names, table heads, scale ticks
    },
  },
  radii: { 'r-hair': 2, 'r-round': 9999 },
  shape: { control: 'r-hair', card: 'r-hair', chip: 'r-hair', sheet: 'r-hair', dot: 'r-round' },
  // No shadows (the sample has none); the late ring on a visit under way is the only one.
  shadows: {
    light: { 'ring-late': [{ x: 0, y: 0, blur: 0, spread: 2, color: 'light.signal' }] },
    dark: { 'ring-late': [{ x: 0, y: 0, blur: 0, spread: 2, color: 'dark.signal' }] },
  },
  elevation: { resting: 'ring-late', floating: 'ring-late' },
  // The sample's steps, by Blueprint's own numbering (--bp-s-1 … --bp-s-10 in the prototype).
  spacing: {
    '0': 0,
    '1': 4,
    '2': 6,
    '3': 8,
    '4': 10,
    '5': 12,
    '6': 16,
    '7': 20,
    '8': 24,
    '9': 32,
    '10': 40,
  },
  touch: { minTarget: 44, primaryTarget: 44 },

  // ── The design system's three layers (system.ts; design-system audit §4a). A raw number lives here
  // only. Light and dark differ in the colours and the rule's width. Every value is the sample's
  // (pages/ux-full tokens.css, DESIGN.md §8) unless it says extension. ──
  weights: { body: 400, figure: 500, strong: 600, total: 700 },
  leading: { tight: 1.1, snug: 1.25, list: 1.4, body: 1.5 },
  // §8: one control size (34); a choice in a pop-over (30); a field (38); a small control (26); a tap
  // target on the iPad and in a chat card (44); a glyph (16).
  sizes: { icon: 16, sm: 26, choice: 30, control: 34, field: 38, tap: 44 },
  // the rule is 1.25 on the light sheet and 1 on the dark one (Amir, 24 Oct); icon is an extension
  strokes: {
    light: { hair: 1, rule: 1.25, strong: 2.5, mark: 3, focus: 2, icon: 1.5 },
    dark: { hair: 1, rule: 1, strong: 2.5, mark: 3, focus: 2, icon: 1.5 },
  },
  // extension (the sample has no layers): each layer's cast, further out the higher it sits
  layers: { pop: 4, panel: 6, dialog: 8 },
  meaning: {
    // lines: structure is the rule, detail a hairline; waiting is dashed; a problem is red
    'line-rule': '{w.rule} solid {color.bp-stroke}',
    hair: '{w.hair} solid {color.bp-rule}',
    dash: '{w.rule} dashed {color.bp-stroke}',
    'line-signal': '{w.rule} solid {color.bp-signal}',
    'dash-signal': '{w.rule} dashed {color.bp-signal}',
    'line-strong': '{w.strong} solid {color.bp-ink}',
    // an area waiting on you (set-up not finished, a change not saved till Done; DESIGN.md §2): the
    // waiting drawing at an area's size, dashed all round and shaded in the paper grid's colour. Never
    // red (a problem) and never the interrupt (calls). Amir, 26 Oct.
    'wait-area': '{dash}',
    'wait-area-bg': '{color.bp-grid}',
    'wait-area-pad': '{space.5} {space.6}',
    mark: '{w.mark} solid {color.bp-ink}',
    'focus-line': '{w.focus} solid {color.bp-pen-1}',
    'focus-offset': '{w.focus}',
    'icon-stroke': '{w.icon}',
    hatch: 'repeating-linear-gradient(135deg, {color.bp-ink} 0 1.25px, transparent 1.25px 5px)',
    'hatch-muted': 'repeating-linear-gradient(135deg, {color.bp-grid} 0 1.5px, {color.bp-surface} 1.5px 6px)',
    // controls (§8)
    'control-h': '{size.control}',
    'control-h-sm': '{size.sm}',
    'control-h-touch': '{size.tap}',
    'field-h': '{size.field}',
    'control-pad-x': '{space.5}',
    'control-pad-x-sm': '{space.3}',
    'control-fs': '{fs.table}',
    'control-fw': '{fw.strong}',
    // the sheet and its sections
    'pad-sheet': '{space.8} {space.9} {space.10}',
    'gap-section': '{space.9}',
    'pad-section': '{space.6}',
    'pad-row': '{space.4}',
    // layers: a frame (line-rule) and a cast; a veil behind a panel, a scrim behind a dialog
    'layer-pop': '{layer.pop} {layer.pop} 0 0 {color.bp-cast}',
    'layer-panel': '{layer.panel} {layer.panel} 0 0 {color.bp-cast}',
    'layer-dialog': '{layer.dialog} {layer.dialog} 0 0 {color.bp-cast}',
    veil: '{color.bp-veil}',
    scrim: '{color.bp-scrim}',
    // calls (DESIGN.md §9): the one motion, the ring's pulse and the settle as a ring becomes the call.
    // Each component that moves stops under prefers-reduced-motion; the drawing alone says it.
    'motion-ring': '1600ms',
    'motion-settle': '280ms',
    'motion-curve': 'cubic-bezier(0.2, 0, 0, 1)',
    'interrupt-fill': '{color.bp-interrupt}',
    'interrupt-ink': '{color.bp-on-interrupt}',
    'interrupt-quiet': '{color.bp-interrupt-wash}',
    // the office app's shared stylesheets (office.ts): Riverstone's measures, the same as Riverstone's set
    ...officeMeaning('blueprint'),
    // the bridge (apps/web/src/blueprint/blueprint.css): Riverstone's chosen and now rings (--ring-sel,
    // --ring-now), drawn at Blueprint's focus weight in ink and in signal red
    'bridge-ring-sel': '0 0 0 {w.focus} {color.bp-ink}',
    'bridge-ring-now': '0 0 0 {w.focus} {color.bp-signal}',
  },
  // What each component reads, and nothing else (packages/ui-web/src/components/*/).
  components: {
    // Button: primary (the one filled), secondary (boxed), danger; sm 26, md 34, touch 44
    'btn-h': '{control-h}',
    'btn-h-sm': '{control-h-sm}',
    'btn-h-touch': '{control-h-touch}',
    'btn-pad-x': '{control-pad-x}',
    'btn-pad-x-sm': '{control-pad-x-sm}',
    'btn-fs': '{control-fs}',
    'btn-fs-sm': '{fs.label}',
    'btn-fw': '{control-fw}',
    'btn-lh': '{lh.tight}',
    'btn-glyph': '{size.icon}',
    'btn-stroke': '{icon-stroke}',
    'btn-gap': '{space.2}',
    'btn-border': '{line-rule}',
    'btn-radius': '{radius.r-hair}',
    'btn-bg': '{color.bp-surface}',
    'btn-ink': '{color.bp-ink}',
    'btn-pri-bg': '{color.bp-ink}',
    'btn-pri-ink': '{color.bp-on-ink}',
    'btn-danger': '{color.bp-signal}',
    'btn-danger-border': '{line-signal}',
    'btn-busy-border': '{dash}',
    'btn-disabled-border': '{hair}',
    'btn-disabled-ink': '{color.bp-muted}',
    'btn-underline-offset': '{space.1}',
    'btn-underline': '{w.hair}',
    'btn-pressed-ring': 'inset 0 0 0 {w.rule} {color.bp-stroke}',
    'btn-pri-pressed-ring': 'inset 0 0 0 {w.rule} {color.bp-on-ink}',
    'btn-focus': '{focus-line}',
    'btn-focus-offset': '{focus-offset}',
    // IconButton: 34 × 34, a 16 px glyph; on is filled in ink
    'iconbtn-size': '{control-h}',
    'iconbtn-glyph': '{size.icon}',
    'iconbtn-stroke': '{icon-stroke}',
    'iconbtn-border': '{line-rule}',
    'iconbtn-radius': '{radius.r-hair}',
    'iconbtn-bg': '{color.bp-surface}',
    'iconbtn-ink': '{color.bp-ink}',
    'iconbtn-on-bg': '{color.bp-ink}',
    'iconbtn-on-ink': '{color.bp-on-ink}',
    'iconbtn-hover-ring': 'inset 0 0 0 {w.rule} {color.bp-stroke}',
    'iconbtn-disabled-border': '{hair}',
    'iconbtn-disabled-ink': '{color.bp-muted}',
    'iconbtn-focus': '{focus-line}',
    'iconbtn-focus-offset': '{focus-offset}',
    // Close (round 2, Amir 26 Oct: one ×, top right, no box): the glyph alone in a 34 px target; hover
    // draws its strokes heavier, keyboard focus a bar under it; never a frame, fill or ring
    'close-size': '{iconbtn-size}',
    // at phone widths the library's touch size, as the panel's primary and links (QA-878)
    'close-size-touch': '{control-h-touch}',
    'close-glyph': '{size.icon}',
    'close-stroke': '{icon-stroke}',
    'close-stroke-hover': '{w.focus}',
    'close-inset': 0,
    'close-ink': '{color.bp-ink}',
    'close-focus-bar': 'inset 0 calc(-1 * {w.focus}) 0 0 {color.bp-pen-1}',
    // Segmented: one frame, hairlines between, the one that's on filled in ink
    'seg-h': '{control-h}',
    'seg-pad-x': '{control-pad-x}',
    'seg-fs': '{control-fs}',
    'seg-fw': '{control-fw}',
    'seg-lh': '{lh.tight}',
    'seg-icon-w': '{control-h}',
    'seg-glyph': '{size.icon}',
    'seg-stroke': '{icon-stroke}',
    'seg-border': '{line-rule}',
    'seg-divider': '{hair}',
    'seg-radius': '{radius.r-hair}',
    'seg-bg': '{color.bp-surface}',
    'seg-ink': '{color.bp-muted}',
    'seg-ink-hover': '{color.bp-ink}',
    'seg-on-bg': '{color.bp-ink}',
    'seg-on-ink': '{color.bp-on-ink}',
    'seg-disabled-ink': '{color.bp-rule}',
    'seg-underline-offset': '{space.1}',
    'seg-underline': '{w.hair}',
    'seg-focus': '{focus-line}',
    'seg-focus-offset': '{focus-offset}',
    // Link: a secondary action, an underlined word (DESIGN.md §3a)
    'link-fw': '{fw.strong}',
    'link-offset': '{space.1}',
    'link-thickness': '{w.hair}',
    'link-thickness-hover': '{w.focus}',
    'link-ink': '{color.bp-ink}',
    'link-danger': '{color.bp-signal}',
    'link-disabled-ink': '{color.bp-muted}',
    'link-focus': '{focus-line}',
    'link-focus-offset': '{focus-offset}',
    // a link that stands alone as an action, at phone widths: the touch size
    'link-h-touch': '{control-h-touch}',
    // Choice and ChoiceGroup: a pop-over's choices, 30 px, framed with a hairline; chosen is filled
    'choice-h': '{size.choice}',
    'choice-pad-x': '{space.3}',
    'choice-fs': '{fs.table}',
    'choice-fw': '{fw.body}',
    'choice-lh': '{lh.tight}',
    'choice-border': '{hair}',
    'choice-radius': '{radius.r-hair}',
    'choice-bg': '{color.bp-surface}',
    'choice-ink': '{color.bp-ink}',
    'choice-on-bg': '{color.bp-ink}',
    'choice-on-ink': '{color.bp-on-ink}',
    'choice-on-border': '{w.hair} solid {color.bp-ink}',
    'choice-disabled-ink': '{color.bp-muted}',
    'choice-disabled-border': '{w.hair} dashed {color.bp-rule}',
    'choice-underline-offset': '{space.1}',
    'choice-underline': '{w.hair}',
    'choice-count-gap': '{space.2}',
    'choice-gap': '{space.2}',
    'choice-focus': '{focus-line}',
    'choice-focus-offset': '{focus-offset}',
    'choice-desc-gap': '{space.1}',
    // the price book (DEC-019): a choice that waits on someone ("Asked by techs") is drawn dashed
    'choice-wait-border': '{dash}',
    'choice-desc-pad-y': '{space.2}',
    'choice-desc-ink': '{color.bp-muted}',
    'choice-desc-fs': '{fs.caption}',
    'choice-desc-lh': '{lh.snug}',
    'choices-label-fs': '{fs.label}',
    'choices-label-fw': '{fw.strong}',
    'choices-label-lh': '{lh.list}',
    'choices-label-ink': '{color.bp-muted}',
    'choices-label-gap': '{space.2}',
    // a group's description under its choices: a field's description, the same muted line
    'choices-desc-ink': '{input-help-ink}',
    'choices-desc-fs': '{input-help-fs}',
    'choices-desc-lh': '{input-help-lh}',
    // ActionGroup: a labelled row of small actions (a message's Put in), its label as a field's
    'actions-label-fs': '{fs.label}',
    'actions-label-fw': '{fw.strong}',
    'actions-label-lh': '{lh.list}',
    'actions-label-ink': '{color.bp-muted}',
    'actions-label-gap': '{space.2}',
    'actions-gap': '{space.2}',
    // Popover: a pick list under its button, on the pop layer
    'pop-w': 380,
    'pop-w-menu': '{menu-w}',
    'pop-max-w': '100vw',
    'pop-pad': '{space.5}',
    'pop-gap': '{space.5}',
    'pop-offset': '{space.1}',
    // Layer: a frame and a cast, Close in the corner; pop (a pop-over, a menu, a hover card), panel
    // (the side panel) and dialog (a modal)
    'layer-frame': '{line-rule}',
    'layer-bg': '{color.bp-surface}',
    'layer-ink': '{color.bp-ink}',
    'layer-radius': '{radius.r-hair}',
    'layer-pad-pop': '{pop-pad}',
    'layer-cast-pop': '{layer-pop}',
    'layer-cast-panel': '{layer-panel}',
    'layer-cast-dialog': '{layer-dialog}',
    'layer-veil': '{veil}',
    'layer-scrim': '{scrim}',
    'layer-close-room': '{iconbtn-size}',
    // round 2: a panel that takes the focus shows it inside its frame
    'sheet-focus': '{focus-line}',
    'sheet-focus-offset': 'calc(-1 * {focus-offset})',
    'sheet-w': 380,
    // round 2: a record's detail (a visit's, a history entry's) wants room for its tables
    // a record's detail: 480 wide, the window's width on a phone
    'sheet-w-wide-max': 480,
    'sheet-w-wide': 'min({sheet-w-wide-max}, 100vw)',
    'sheet-pad': '{space.7}',
    'dialog-w': 560,
    'dialog-pad': '{space.8}',
    // extension (job page round): a dialog's sizes, the room it keeps from the screen's edge, and its
    // tallest, past which its body scrolls inside the frame (the cast stays drawn)
    'dialog-w-sm': 440,
    'dialog-w-lg': 720,
    'dialog-w-fill': 1180,
    'dialog-gutter': '{space.6}',
    'dialog-max-h': 'calc(100dvh - {space.9})',
    // Menu: a list of actions on the pop layer, under its button or at the pointer (a right-click)
    'menu-w': 280,
    'menu-bleed': 'calc(-1 * {pop-pad})',
    'menu-head-h': '{control-h}',
    'menu-head-fs': '{fs.label}',
    'menu-head-fw': '{fw.strong}',
    'menu-head-lh': '{lh.tight}',
    'menu-head-ink': '{color.bp-muted}',
    'menu-head-rule': '{hair}',
    'menu-list-pad': '{space.2}',
    'menu-item-pad-y': '{space.3}',
    'menu-item-pad-x': '{space.6}',
    'menu-item-fs': '{fs.table}',
    'menu-item-fw': '{fw.body}',
    'menu-item-lh': '{lh.list}',
    'menu-ink': '{color.bp-ink}',
    'menu-hover-bg': '{color.bp-grid}',
    'menu-reason-fs': '{fs.caption}',
    'menu-reason-lh': '{lh.snug}',
    'menu-reason-ink': '{color.bp-muted}',
    'menu-disabled-ink': '{color.bp-muted}',
    'menu-sep': '{hair}',
    'menu-sep-gap': '{space.2}',
    'menu-focus': '{focus-line}',
    'menu-focus-offset': 'calc(-1 * {focus-offset})',
    'menu-hint-gap': '{space.5}',
    'menu-pressed-ring': 'inset 0 0 0 {w.rule} {color.bp-stroke}',
    // Dialog: the patterns on the dialog layer (a decision, a form): the question, its words, the actions
    'dlg-gap': '{space.6}',
    'dlg-title-fs': '{fs.h3}',
    'dlg-title-fw': '{fw.strong}',
    'dlg-title-lh': '{lh.snug}',
    'dlg-body-fs': '{fs.body}',
    'dlg-body-lh': '{lh.body}',
    'dlg-ink': '{color.bp-ink}',
    'dlg-muted': '{color.bp-muted}',
    'dlg-fields-gap': '{space.6}',
    'dlg-actions-gap': '{space.3}',
    'dlg-foot-pad': '{space.6}',
    'dlg-foot-rule': '{hair}',
    'dlg-error-ink': '{color.bp-signal}',
    'dlg-error-fs': '{fs.table}',
    'dlg-error-fw': '{fw.strong}',
    // the footer's sentence: exactly what Save writes, or why it's off (timesheet rules §3)
    'dlg-note-fs': '{fs.body-sm}',
    'dlg-note-lh': '{lh.list}',
    // Field: a labelled text box, text area or select, 38 tall (§8's field)
    'input-h': '{field-h}',
    'input-pad-x': '{space.4}',
    'input-pad-y': '{space.3}',
    'input-area-min-h': 88,
    'input-border': '{line-rule}',
    'input-radius': '{radius.r-hair}',
    'input-bg': '{color.bp-surface}',
    'input-ink': '{color.bp-ink}',
    'input-placeholder-ink': '{color.bp-muted}',
    'input-fs': '{fs.body}',
    'input-lh': '{lh.list}',
    'input-gap': '{space.2}',
    'input-label-fs': '{fs.label}',
    'input-label-fw': '{fw.strong}',
    'input-label-lh': '{lh.list}',
    'input-label-ink': '{color.bp-muted}',
    'input-help-fs': '{fs.body-sm}',
    'input-help-lh': '{lh.list}',
    'input-help-ink': '{color.bp-muted}',
    'input-help-fw': '{fw.body}',
    'input-error-ink': '{color.bp-ink}',
    'input-error-border': '{line-signal}',
    'input-error-fw': '{fw.strong}',
    'input-disabled-border': '{hair}',
    'input-disabled-ink': '{color.bp-muted}',
    'input-focus': '{focus-line}',
    'input-focus-offset': 0,
    'input-sr-size': 1,
    // a number's and a time's box beside its words in a row (Setup's settings)
    'input-num-w': 110,
    'input-time-w': 132,
    // a short code's box (a document number's prefix): ten characters
    'input-short-w': 110,
    'input-date-w': 142,
    'input-unit-gap': '{space.7}',
    // Checkbox: a 16 px box, ticked in ink, its words beside it
    'check-size': '{size.icon}',
    'check-border': '{line-rule}',
    'check-radius': '{radius.r-hair}',
    'check-bg': '{color.bp-surface}',
    'check-on-bg': '{color.bp-ink}',
    'check-on-ink': '{color.bp-on-ink}',
    'check-stroke': '{w.focus}',
    'check-gap': '{space.3}',
    'check-ink': '{color.bp-ink}',
    'check-fs': '{fs.table}',
    'check-fw': '{fw.body}',
    'check-lh': '{lh.list}',
    'check-nudge': '{w.focus}',
    'check-disabled-border': '{hair}',
    'check-disabled-ink': '{color.bp-muted}',
    'check-focus': '{focus-line}',
    'check-focus-offset': '{focus-offset}',
    'check-touch-h': '{control-h-touch}',
    // PhotoThumb: a photo's small copy, 88 × 66 (44 × 33 in a closed line); chosen is the heavier line
    'thumb-w': 88,
    'thumb-h': 66,
    'thumb-w-sm': 44,
    'thumb-h-sm': 33,
    // a square tile in a PhotoRoll (round 3): three and a bit across the main column at 1280
    'thumb-tile': 160,
    'thumb-tile-phone': 128,
    'thumb-frame': '{hair}',
    'thumb-radius': '{radius.r-hair}',
    'thumb-bg': '{color.bp-surface}',
    'thumb-ink': '{color.bp-ink}',
    'thumb-muted': '{color.bp-muted}',
    'thumb-hover-ring': 'inset 0 0 0 {w.rule} {color.bp-stroke}',
    'thumb-on-ring': 'inset 0 0 0 {w.strong} {color.bp-ink}',
    'thumb-focus': '{focus-line}',
    'thumb-focus-offset': '{focus-offset}',
    'thumb-tag-bg': '{color.bp-surface}',
    'thumb-tag-fs': '{fs.caption}',
    'thumb-tag-fw': '{fw.body}',
    'thumb-tag-lh': '{lh.snug}',
    'thumb-tag-pad-x': '{space.1}',
    'thumb-glyph': 12,
    'thumb-glyph-pad': '{space.1}',
    'thumb-stroke': '{icon-stroke}',
    'thumb-fail-gap': '{space.1}',
    // a photo still on its way up: a dashed frame (waiting, DESIGN.md §2) with its words
    'thumb-wait-frame': '{dash}',
    'thumb-more-fs': '{fs.table}',
    'thumb-more-fw': '{fw.strong}',
    // PhotoGroup: a visit's photos in one row, its stages side by side, each with a count
    'photos-head-w': 96,
    'photos-head-pad': 18,
    'photos-gap': '{space.5}',
    'photos-row-pad': '{space.4}',
    'photos-rule': '{hair}',
    'photos-title-fs': '{fs.table}',
    'photos-title-fw': '{fw.strong}',
    'photos-title-lh': '{lh.snug}',
    'photos-sub-fs': '{fs.body-sm}',
    'photos-sub-fw': '{fw.body}',
    'photos-ink': '{color.bp-ink}',
    'photos-muted': '{color.bp-muted}',
    'photos-stage-gap-x': '{space.8}',
    'photos-stage-gap-y': '{space.3}',
    'photos-label-h': 14,
    'photos-label-fs': '{fs.caption}',
    'photos-label-fw': '{fw.strong}',
    'photos-label-lh': '{lh.snug}',
    'photos-label-gap': '{space.1}',
    'photos-thumb-gap': '{space.3}',
    'photos-strip-gap': '{space.2}',
    'photos-empty-fs': '{fs.body-sm}',
    // PhotoRoll (round 3): every photo in one sideways line of square tiles, the time under each
    'roll-gap': '{space.3}',
    'roll-room': 'calc(2 * {w.focus})',
    'roll-pull': 'calc(-2 * {w.focus})',
    'roll-scroll-pad': '{space.2}',
    'roll-snap-pad': 'calc(2 * {w.focus})',
    'roll-when-gap': '{space.1}',
    'roll-when-fs': '{fs.caption}',
    'roll-when-lh': '{lh.snug}',
    'roll-muted': '{color.bp-muted}',
    'roll-empty-fs': '{fs.body-sm}',
    // GalleryTools: over a job's photos, Show, Taken by and Order side by side, then how many show
    'gallery-gap': '{space.3}',
    'gallery-shown-gap': '{space.3}',
    'gallery-shown-fs': '{fs.body-sm}',
    'gallery-shown-lh': '{lh.snug}',
    'gallery-shown-ink': '{color.bp-muted}',
    // PhotoViewer: one photo full size on the dialog layer, its facts beside it, every photo under it
    'viewer-w': '{dialog-w-fill}',
    'viewer-ink': '{color.bp-ink}',
    'viewer-muted': '{color.bp-muted}',
    'viewer-head-gap': '{space.5}',
    'viewer-title-fs': '{fs.h3}',
    'viewer-title-fw': '{fw.strong}',
    'viewer-title-lh': '{lh.snug}',
    'viewer-count-fs': '{fs.table}',
    'viewer-gap': '{space.6}',
    'viewer-info-w': 260,
    'viewer-info-gap': '{space.5}',
    'viewer-info-fs': '{fs.table}',
    'viewer-info-lh': '{lh.list}',
    'viewer-label-fs': '{fs.label}',
    'viewer-label-fw': '{fw.strong}',
    'viewer-rule': '{line-rule}',
    'viewer-hair': '{hair}',
    'viewer-stage-bg': '{color.bp-bg}',
    'viewer-chrome-h': 236,
    'viewer-stage-h': 'calc(100dvh - {viewer-chrome-h})',
    'viewer-stage-min-h': 260,
    'viewer-stage-h-sm': '40dvh',
    'viewer-hint-fs': '{fs.body-sm}',
    'viewer-focus': '{focus-line}',
    'viewer-focus-offset': 'calc(-1 * {focus-offset})',
    'viewer-nav-inset': '{space.4}',
    'viewer-nav-top': '50%',
    'viewer-wait-frame': '{dash}',
    'viewer-wait-pad': '{space.6}',
    'viewer-strip-gap': '{space.2}',
    'viewer-strip-pad': '{space.4}',
    'viewer-strip-ring': '{space.1}',
    'viewer-fade': 'opacity 160ms cubic-bezier(0, 0, 0, 1)',
    // Area: a record page's section, open or closed to a line; a rule above it, never a box
    'area-rule': '{line-rule}',
    'area-ink': '{color.bp-ink}',
    'area-muted': '{color.bp-muted}',
    'area-head-h': 46,
    'area-head-pad': '{space.3}',
    'area-head-gap': '{space.5}',
    'area-row-gap': '{space.1}',
    'area-title-fs': '{fs.h3}',
    'area-title-fw': '{fw.strong}',
    'area-title-lh': '{lh.tight}',
    'area-count-fw': '{fw.body}',
    'area-chev': '{size.icon}',
    'area-chev-gap': '{space.4}',
    'area-chev-stroke': '{icon-stroke}',
    'area-chev-open': 'rotate(90deg)',
    'area-chev-motion': 'transform 120ms cubic-bezier(0.2, 0, 0, 1)',
    'area-sum-fs': '{fs.body-lg}',
    'area-sum-lh': '{lh.snug}',
    'area-acts-gap': '{space.6}',
    'area-acts-fs': '{fs.body}',
    'area-body-gap': '{space.6}',
    'area-body-pad': '{space.8}',
    'area-underline': '{w.hair}',
    'area-underline-offset': '{space.1}',
    'area-focus': '{focus-line}',
    'area-focus-offset': '{focus-offset}',
    // StateMark: a state drawn small beside its word, as the board draws a visit in it
    'smark-w': '{size.icon}',
    'smark-h': 12,
    'smark-line': '{line-rule}',
    'smark-dash': '{dash}',
    'smark-fill': '{color.bp-ink}',
    'smark-hatch': '{hatch}',
    'smark-gone-line': '{hair}',
    'smark-strike':
      'linear-gradient(to top right, transparent 46%, {color.bp-muted} 46% 54%, transparent 54%)',
    'smark-radius': '{radius.r-hair}',
    'smark-nudge': 'middle',
    // truck stock (BL-203): a problem beside its words, which stay ink: red is off small text (it is
    // 4.47:1 on the desk and 4.04:1 on a wash), so the problem is a short red bar, the word says it
    'smark-problem-line': '{w.mark} solid {color.bp-signal}',
    'smark-problem-gap': '{space.2}',
    // KeyValue: labelled facts on hairlines (grid) or a thing and its value (spread)
    'kv-label-w': 112,
    // grid's label column fits its longest label up to this cap, past which a label wraps (BL-189); on a
    // phone it stays kv-label-w
    'kv-label-max': 200,
    // a group's subhead inside the grid ("Budgeted each working day"): a hairline above it
    'kv-head-top': '{space.7}',
    'kv-head-bottom': '{space.3}',
    'kv-head-pad': '{space.5}',
    'kv-head-fs': '{fs.body}',
    'kv-head-fw': '{fw.strong}',
    'kv-gap': '{space.4}',
    // stacked on a phone: the label just above its value
    'kv-stack-gap': '{space.1}',
    'kv-row-pad': '{space.3}',
    'kv-row-pad-tight': '{space.1}',
    'kv-divider': '{hair}',
    'kv-label-fs': '{fs.label}',
    'kv-label-fw': '{fw.strong}',
    'kv-label-ink': '{color.bp-muted}',
    'kv-fs': '{fs.table}',
    'kv-value-fw': '{fw.body}',
    'kv-lh': '{lh.list}',
    'kv-ink': '{color.bp-ink}',
    // truck stock (BL-203): figures, a place's facts side by side (the title block's figures, catalogued)
    'kv-fig-rule': '{line-rule}',
    'kv-fig-min': 140,
    'kv-fig-pad': '{space.5} {space.6}',
    'kv-fig-gap': '{space.1}',
    'kv-fig-fs': '{fs.figure}',
    'kv-fig-fw': '{fw.figure}',
    'kv-fig-lh': '{lh.tight}',
    'kv-sub-fs': '{fs.label}',
    // rows (BL-189, Sales tax's returns): amounts one under another, the amount right-aligned and bold
    'kv-rows-gap': '{space.3}',
    'kv-rows-col-gap': '{space.6}',
    'kv-rows-fs': '{fs.body}',
    'kv-rows-lh': '{lh.body}',
    'kv-rows-fw': '{fw.strong}',
    // ContactRow: a number or an address, how it's used, Call / Text / Email
    'contact-gap': '{space.5}',
    'contact-pad': '{space.4}',
    'contact-rule': '{hair}',
    'contact-ink': '{color.bp-ink}',
    'contact-muted': '{color.bp-muted}',
    'contact-fs': '{fs.body}',
    'contact-note-fs': '{fs.body-sm}',
    'contact-lh': '{lh.list}',
    'contact-acts-gap': '{space.5}',
    // TaskRow: a follow-up ticked off; overdue in red
    'task-pad': '{space.4}',
    'task-rule': '{hair}',
    'task-ink': '{color.bp-ink}',
    'task-muted': '{color.bp-muted}',
    'task-bad': '{color.bp-signal}',
    'task-meta-fs': '{fs.body-sm}',
    'task-meta-fw': '{fw.body}',
    'task-bad-fw': '{fw.strong}',
    // SiteBlock: the address, a drawn street map, the note for whoever arrives, what's at the address
    'site-gap': '{space.5}',
    'site-ink': '{color.bp-ink}',
    'site-muted': '{color.bp-muted}',
    'site-fs': '{fs.table}',
    'site-sub-fs': '{fs.label}',
    'site-lh': '{lh.list}',
    'site-head-fw': '{fw.strong}',
    'site-map-frame': '{hair}',
    'site-map-bg': '{color.bp-bg}',
    'site-street': '{color.bp-rule}',
    'site-street-w': 6,
    'site-lot': '{color.bp-stroke}',
    'site-lot-w': '{w.rule}',
    'site-pin': '{color.bp-ink}',
    'site-in-pad': '{space.5}',
    'site-in-mark': '{mark}',
    // FormRecord: a form or a signature as a line that opens to its answers
    'record-h': '{control-h}',
    'record-gap': '{space.3}',
    'record-rule': '{hair}',
    'record-ink': '{color.bp-ink}',
    'record-muted': '{color.bp-muted}',
    'record-fs': '{fs.table}',
    'record-title-fw': '{fw.strong}',
    'record-indent': '{space.8}',
    'record-body-pad': '{space.5}',
    'record-underline': '{w.hair}',
    'record-underline-offset': '{space.1}',
    'record-focus': '{focus-line}',
    'record-focus-offset': '{focus-offset}',
    // HistoryItem: one thing that happened: when, its kind, who did what, what to know
    'hist-time-w': 84,
    // round 2: a row's head opens its detail (an underlined head on hover)
    'hist-underline': '{w.hair}',
    'hist-underline-offset': 3,
    'hist-kind-w': '{size.icon}',
    'hist-gap': '{space.5}',
    'hist-pad': '{space.5}',
    'hist-pad-x': '{space.3}',
    'hist-rule': '{hair}',
    'hist-ink': '{color.bp-ink}',
    'hist-muted': '{color.bp-muted}',
    'hist-fs': '{fs.body}',
    'hist-lh': '{lh.list}',
    'hist-time-fs': '{fs.body-sm}',
    'hist-detail-fs': '{fs.body-sm}',
    'hist-name-fw': '{fw.strong}',
    'hist-glyph': '{size.icon}',
    'hist-stroke': '{icon-stroke}',
    'hist-glyph-nudge': '{w.focus}',
    'hist-body-gap': '{space.1}',
    'hist-strike': '{w.hair}',
    'hist-sr-size': 1,
    'hist-quote-gap': '{space.2}',
    'hist-quote-pad': '{space.5}',
    'hist-quote-mark': '{w.focus} solid {color.bp-rule}',
    'hist-extra-gap': '{space.5}',
    'hist-pin-rule': '{line-rule}',
    'hist-hit-bg': '{color.bp-grid}',
    'hist-hit-mark': 'inset {w.mark} 0 0 0 {color.bp-ink}',
    'hist-focus': '{focus-line}',
    'hist-focus-offset': 'calc(-1 * {focus-offset})',
    // round 2, opens in place (Amir 26 Oct, pages/job-history-option): the whole row is the disclosure,
    // Area's chevron at its end, an open row marked down its left edge, its entry under its words
    'hist-chev-w': '{area-chev}',
    'hist-hover-bg': '{color.bp-grid}',
    'hist-open-mark': 'inset {w.mark} 0 0 0 {color.bp-ink}',
    'hist-open-pad-end': '{space.3}',
    'hist-entry-inset': 'calc({hist-time-w} + {hist-kind-w} + 2 * {hist-gap})',
    'hist-entry-inset-phone': 'calc({hist-pad-x} + {hist-kind-w} + {hist-gap})',
    'hist-entry-end': 'calc({hist-chev-w} + {hist-gap})',
    'hist-entry-pad': '{space.3} 0 {space.6}',
    'hist-entry-pad-phone': '0 0 {space.6}',
    // HistoryFeed: a record's history by day: kinds and counts, a search, the latest few, Show all
    'feed-gap': '{space.6}',
    'feed-bar-gap': '{space.5}',
    'feed-search-w': 240,
    'feed-search-min-w': 180,
    'feed-ink': '{color.bp-ink}',
    'feed-muted': '{color.bp-muted}',
    'feed-fs': '{fs.body}',
    'feed-day-pad': '{space.6}',
    'feed-day-pad-end': '{space.3}',
    'feed-day-rule': '{hair}',
    'feed-day-fs': '{fs.label}',
    'feed-day-fw': '{fw.strong}',
    'feed-day-lh': '{lh.list}',
    'feed-tools-gap': '{space.6}',
    // Composer: who it's for, the box, the button
    'compose-gap': '{space.4}',
    // LineTable (job page round): lines, parts, the sold/billed/used compare, a visit's times
    'lines-fs': '{fs.table}',
    'lines-lh': '{lh.list}',
    'lines-ink': '{color.bp-ink}',
    'lines-muted': '{color.bp-muted}',
    'lines-head-fs': '{fs.caption}',
    'lines-head-fw': '{fw.strong}',
    'lines-sub-fw': '{fw.body}',
    'lines-pad-y': '{space.3}',
    'lines-pad-x': '{space.5}',
    'lines-rule': '{hair}',
    'lines-head-rule': '{line-rule}',
    'lines-foot-rule': '{line-rule}',
    'lines-foot-fw': '{fw.strong}',
    'lines-focus': '{focus-line}',
    'lines-focus-offset': '{focus-offset}',
    // truck stock (BL-203): rows in groups under a head, a note under a row, a line taken off
    'lines-group-pad-top': '{space.6}',
    'lines-group-fs': '{fs.body}',
    'lines-group-fw': '{fw.strong}',
    'lines-says-gap': '{space.3}',
    'lines-note-fs': '{fs.body-sm}',
    'lines-noted-pad-y': '{space.1}',
    // a report's sums (BL-189): a section's sum over a rule, strong; the report's total larger
    'lines-sum-rule': '{line-rule}',
    'lines-sum-fw': '{fw.strong}',
    'lines-total-fs': '{fs.body-lg}',
    // Totals: an invoice's sums, right-aligned under its lines
    'totals-w': 320,
    'totals-top': '{space.4}',
    'totals-fs': '{fs.table}',
    'totals-ink': '{color.bp-ink}',
    'totals-muted': '{color.bp-muted}',
    'totals-pad': '{space.1}',
    'totals-gap': '{space.5}',
    'totals-strong-fw': '{fw.strong}',
    'totals-due-rule': '{line-rule}',
    'totals-due-pad': '{space.3}',
    'totals-due-fs': '{fs.body-lg}',
    'totals-due-fw': '{fw.total}',
    // VisitRow: a visit in the job's Visits, beside the board's VisitBlock
    'vrow-block-w': 184,
    'vrow-block-h': 56,
    'vrow-gap': '{space.6}',
    'vrow-pad': '{space.4}',
    'vrow-rule': '{hair}',
    'vrow-ink': '{color.bp-ink}',
    'vrow-muted': '{color.bp-muted}',
    'vrow-fs': '{fs.body}',
    'vrow-lh': '{lh.list}',
    'vrow-what-fw': '{fw.strong}',
    'vrow-how-fs': '{fs.table}',
    'vrow-ins-fs': '{fs.body-sm}',
    'vrow-came-fs': '{fs.caption}',
    'vrow-came-fw': '{fw.strong}',
    'vrow-came-line': '{dash}',
    'vrow-toggle': '{size.control}',
    'vrow-toggle-hover': '{color.bp-grid}',
    'vrow-focus': '{focus-line}',
    'vrow-focus-offset': '{focus-offset}',
    'vrow-radius': '{radius.r-hair}',
    'vrow-more-pad': '{space.6}',
    'vrow-more-gap': '{space.4}',
    'vrow-links-gap': '{space.6}',
    'vrow-links-fs': '{fs.table}',
    // JobHeader: the top of a job (crumb, title, who, where it stands, one main button)
    'jobhead-gap': '{space.8}',
    'jobhead-acts-gap': '{space.3}',
    'jobhead-pad-end': '{space.6}',
    'jobhead-rule': '{line-rule}',
    'jobhead-ink': '{color.bp-ink}',
    'jobhead-muted': '{color.bp-muted}',
    'jobhead-crumb-fs': '{fs.label}',
    'jobhead-crumb-fw': '{fw.body}',
    'jobhead-title-fs': '{fs.h1}',
    // round 2: the kicker over the title (job type · job number), and the state line under who
    'jobhead-kicker-fs': '{fs.label}',
    'jobhead-kicker-fw': '{fw.strong}',
    'jobhead-kicker-gap': '{space.1}',
    'jobhead-meta-gap': '{space.2}',
    'jobhead-meta-fs': '{fs.body-sm}',
    'jobhead-meta-fw': '{fw.body}',
    'jobhead-state-gap': '{space.6}',
    'jobhead-title-fw': '{fw.strong}',
    'jobhead-title-lh': '{lh.tight}',
    'jobhead-title-gap': '{space.3}',
    'jobhead-title-gap-end': '{space.2}',
    'jobhead-who-fs': '{fs.body-lg}',
    'jobhead-who-fw': '{fw.strong}',
    'jobhead-stand-gap': '{space.4}',
    'jobhead-stand-fs': '{fs.body}',
    'jobhead-stand-fw': '{fw.strong}',
    'jobhead-mark-gap': '{space.3}',
    'jobhead-sep-gap': '{space.3}',
    'jobhead-bad': '{color.bp-signal}',
    // StoryStrip: how the job unfolded, in one drawn line
    'story-h': 58,
    'story-top': '{space.5}',
    'story-stop-min': 72,
    'story-stop-max': 132,
    'story-visit-min': 84,
    'story-visit-max': 150,
    'story-tail-min': '{space.6}',
    'story-row-label': 18,
    'story-row-mark': 16,
    'story-line-top': 25,
    'story-line': '{hair}',
    'story-wait': '{dash}',
    'story-node': 9,
    'story-node-fill': '{color.bp-ink}',
    'story-node-line': '{line-rule}',
    'story-node-radius': '{radius.r-round}',
    'story-gone-fill': '{color.bp-surface}',
    'story-gone-line': '{w.rule} solid {color.bp-muted}',
    'story-bar-h': 12,
    'story-bar-end': '{space.4}',
    'story-bar-mark': '{w.mark}',
    'story-bar-line': '{w.hair}',
    'story-bar-done': '{hatch-muted}',
    'story-bar-radius': '{radius.r-hair}',
    'story-bar-bg': '{color.bp-surface}',
    'story-pen-1': '{color.bp-pen-1}',
    'story-pen-2': '{color.bp-pen-2}',
    'story-pen-3': '{color.bp-pen-3}',
    'story-pen-4': '{color.bp-pen-4}',
    'story-pen-5': '{color.bp-pen-5}',
    'story-pen-6': '{color.bp-pen-6}',
    'story-pen-7': '{color.bp-pen-7}',
    'story-pen-8': '{color.bp-pen-8}',
    'story-ink': '{color.bp-ink}',
    'story-muted': '{color.bp-muted}',
    'story-label-fs': '{fs.label}',
    'story-label-fw': '{fw.strong}',
    'story-day-fs': '{fs.caption}',
    'story-text-pad': '{space.3}',
    'story-underline': '{w.hair}',
    'story-underline-offset': '{space.1}',
    'story-focus': '{focus-line}',
    'story-focus-offset': 'calc(-1 * {focus-offset})',
    'story-all-pad': '{space.6}',
    'story-all-gap': '{space.4}',
    'story-all-rule': '{hair}',
    'story-all-fs': '{fs.table}',
    'story-scroll-pad': '{space.2}',
    // rich (round 2): who and outcome under each stop
    'story-rich-max': 180,
    'story-rich-visit-min': 132,
    // StuckBar: the job's name, state and main button while the top is scrolled away
    'stuck-h': 46,
    'stuck-gap': '{space.5}',
    'stuck-pad-x': '{space.8}',
    'stuck-bg': '{color.bp-surface}',
    'stuck-rule': '{line-rule}',
    'stuck-ink': '{color.bp-ink}',
    'stuck-title-fw': '{fw.strong}',
    'stuck-fs': '{fs.table}',
    'stuck-strong-fw': '{fw.strong}',
    'stuck-bad': '{color.bp-signal}',
    'stuck-layer': 25,
    'stuck-motion': 'transform 120ms cubic-bezier(0.2, 0, 0, 1)',
    'stuck-hidden': 'translateY(-100%)',
    // ReviewFixList: what the owner clears before completing a job, each with its fix
    'fixes-gap': '{space.6}',
    'fixes-ink': '{color.bp-ink}',
    'fixes-muted': '{color.bp-muted}',
    'fixes-bad': '{color.bp-signal}',
    'fixes-fs': '{fs.table}',
    'fixes-sub-fs': '{fs.body-sm}',
    'fixes-quote-pad': '{space.5}',
    'fixes-quote-mark': '{w.mark} solid {color.bp-pen-1}',
    'fixes-row-gap': '{space.4}',
    'fixes-row-pad': '{space.4}',
    'fixes-rule': '{hair}',
    'fixes-n': 20,
    'fixes-n-col': 22,
    'fixes-n-line': '{dash-signal}',
    'fixes-n-fs': '{fs.caption}',
    'fixes-n-fw': '{fw.total}',
    'fixes-ok-line': '{w.rule} solid {color.bp-muted}',
    'fixes-ok-bg': '{hatch-muted}',
    'fixes-round': '{radius.r-round}',
    'fixes-title-fw': '{fw.strong}',
    'fixes-acts-gap': '{space.5}',
    'fixes-foot-pad': '{space.4}',
    'fixes-sr-size': 1,
    // EstimateOptions: an estimate's options side by side, the signed one heavier
    'estopt-gap': '{space.5}',
    'estopt-pad': '{space.5}',
    'estopt-line': '{hair}',
    'estopt-won-line': '{line-strong}',
    'estopt-ink': '{color.bp-ink}',
    'estopt-muted': '{color.bp-muted}',
    'estopt-fs': '{fs.table}',
    'estopt-sub-fs': '{fs.body-sm}',
    'estopt-head-fw': '{fw.strong}',
    'estopt-price-fs': '{fs.figure}',
    'estopt-price-fw': '{fw.figure}',
    'estopt-price-lh': '{lh.tight}',
    'estopt-price-gap': '{space.2}',
    'estopt-list-pad': '{space.6}',
    'estopt-list-gap': '{space.3}',
    'estopt-radius': '{radius.r-hair}',
    // RecordLayout (src/patterns, was RecordLayout): the work on the left, people and place on the right
    'recordlayout-side-w': 320,
    'recordlayout-side-w-sm': 296,
    'recordlayout-gap': '{space.8}',
    'recordlayout-rule': '{hair}',
    'recordlayout-stack-rule': '{line-rule}',
    'recordlayout-top': '{space.2}',
    // JobPage: the few lines only the job page draws (the margin, the invoice's match, a foot)
    'jobpage-gap': '{space.5}',
    'jobpage-fs': '{fs.table}',
    'jobpage-sub-fs': '{fs.body-sm}',
    'jobpage-ink': '{color.bp-ink}',
    'jobpage-muted': '{color.bp-muted}',
    'jobpage-bad': '{color.bp-signal}',
    'jobpage-strong-fw': '{fw.strong}',
    'jobpage-rule': '{hair}',
    'jobpage-pad': '{space.4}',
    'jobpage-sig-w': 240,
    'jobpage-sheet-pad': '{space.8}',
    'jobpage-sheet-pad-phone': '{space.6}',
    'jobpage-sheet-bg': '{color.bp-surface}',
    'jobpage-sheet-line': '{line-rule}',
    // TimeBar (timesheet round): a part of a technician's clocked day on a timeline. Status is drawn,
    // never coloured (timesheet rules §3): work hatched in the person's pen, a drive half height, shop
    // and supply house a muted hatch, lunch no box but a baseline, running solid, no clock-out a red
    // dashed outline, to check a red line and red words, a change not saved yet a dashed ink ghost.
    'tbar-line': '{w.rule}',
    'tbar-selected-line': '{w.mark}',
    'tbar-ghost-line': '{w.mark}',
    'tbar-radius': '{radius.r-hair}',
    'tbar-style': 'solid',
    'tbar-open-style': 'dashed',
    'tbar-drive-top': '50%',
    'tbar-bg': '{color.bp-surface}',
    'tbar-ink': '{color.bp-ink}',
    'tbar-muted': '{color.bp-muted}',
    'tbar-bad': '{color.bp-signal}',
    'tbar-quiet': '{color.bp-grid}',
    'tbar-quiet-line': '{color.bp-stroke}',
    'tbar-pen': '{color.bp-ink}',
    'tbar-pen-1': '{color.bp-pen-1}',
    'tbar-pen-2': '{color.bp-pen-2}',
    'tbar-pen-3': '{color.bp-pen-3}',
    'tbar-pen-4': '{color.bp-pen-4}',
    'tbar-pen-5': '{color.bp-pen-5}',
    'tbar-pen-6': '{color.bp-pen-6}',
    'tbar-pen-7': '{color.bp-pen-7}',
    'tbar-pen-8': '{color.bp-pen-8}',
    'tbar-on-pen': '{color.bp-on-pen}',
    'tbar-hatch-line': 1,
    'tbar-hatch-gap': 8,
    'tbar-quiet-hatch-line': 1.5,
    'tbar-quiet-hatch-gap': 6,
    'tbar-baseline': '{w.mark}',
    'tbar-pad-x': '{space.1}',
    'tbar-pad-y': '{space.1}',
    'tbar-fs': '{fs.caption}',
    'tbar-fw': '{fw.strong}',
    'tbar-lh': '{lh.snug}',
    'tbar-underline': '{w.hair}',
    'tbar-underline-offset': 2,
    'tbar-focus': '{focus-line}',
    'tbar-focus-offset': '{focus-offset}',
    // QuickEdit (timesheet round): one person's day in a FormDialog; the strip, its notes, Fix, Change,
    // Add time and Why
    // TimeTrack (timesheet round): a timeline's row, placing by minutes, stacking overlaps.
    'ttrack-row-h': 40,
    'ttrack-row-h-compact': '{lane-h-compact}',
    'ttrack-row-gap': '{space.1}',
    'ttrack-hour': '{color.bp-grid}',
    'ttrack-grid-w': '{w.hair}',
    'ttrack-mark-w': '{w.rule}',
    'ttrack-mark-style': 'dashed',
    'ttrack-mark-ink': '{color.bp-muted}',
    'ttrack-mark-pad': '{space.1}',
    'ttrack-mark-fs': '{fs.caption}',
    // DayStrip (timesheet round): the quick edit's day: Booked over Clocked.
    'dstrip-label-w': 64,
    'dstrip-gap': '{space.2}',
    'dstrip-gap-narrow': '{space.1}',
    'dstrip-muted': '{color.bp-muted}',
    'dstrip-fs': '{fs.caption}',
    'dstrip-slot-inset': 7,
    'dstrip-slot-gap': '{space.1}',
    'qedit-gap': '{space.5}',
    'qedit-sub-ink': '{color.bp-muted}',
    'qedit-sub-fs': '{fs.body-sm}',
    'qedit-note-fs': '{fs.body-sm}',
    'qedit-note-ink': '{color.bp-ink}',
    'qedit-muted': '{color.bp-muted}',
    'qedit-bad': '{color.bp-signal}',
    'qedit-head-fw': '{fw.strong}',
    'qedit-slot-h': 112,
    'qedit-rule': '{hair}',
    'qedit-steps-gap': '{space.3}',
    // PeriodHead: the top of the Timesheet and of Hours
    'phead-gap': '{space.8}',
    'phead-pad-end': '{space.6}',
    'phead-rule': '{line-rule}',
    'phead-ink': '{color.bp-ink}',
    'phead-muted': '{color.bp-muted}',
    'phead-bad': '{color.bp-signal}',
    'phead-crumb-fs': '{fs.label}',
    'phead-title-fs': '{fs.h1}',
    'phead-title-fw': '{fw.strong}',
    'phead-title-lh': '{lh.tight}',
    'phead-title-gap': '{space.3}',
    'phead-line-gap': '{space.2}',
    'phead-pay-fs': '{fs.body}',
    'phead-facts-gap': '{space.5}',
    'phead-fact-gap': '{space.8}',
    'phead-label-fs': '{fs.caption}',
    'phead-value-fs': '{fs.body-lg}',
    'phead-value-fw': '{fw.strong}',
    'phead-acts-gap': '{space.4}',
    // ToFixList: what's left before the period locks, one row per problem
    'tofix-who-w': 160,
    'tofix-day-w': 96,
    'tofix-gap': '{space.4}',
    'tofix-row-pad': '{space.3}',
    'tofix-divider': '{hair}',
    'tofix-ink': '{color.bp-ink}',
    'tofix-fs': '{fs.table}',
    'tofix-day-fw': '{fw.strong}',
    // PeriodDays: a person's period, a row per day on one timeline
    'pdays-day-w': 96,
    'pdays-paid-w': 112,
    'pdays-gap': '{space.4}',
    'pdays-row-gap': '{space.1}',
    'pdays-ink': '{color.bp-ink}',
    'pdays-muted': '{color.bp-muted}',
    'pdays-bad': '{color.bp-signal}',
    'pdays-fs': '{fs.table}',
    'pdays-small-fs': '{fs.caption}',
    'pdays-week-gap': '{space.3}',
    'pdays-week-pad': '{space.2}',
    'pdays-week-rule': '{hair}',
    'pdays-week-fw': '{fw.strong}',
    // TimesheetPage and HoursPage: the areas, one under another
    'tspage-gap': '{space.6}',
    'tspage-max-w': 1240,
    'tspage-sheet-pad': '{space.8}',
    'tspage-sheet-pad-phone': '{space.4}',
    'tspage-sheet-line': '{line-rule}',
    'tspage-sheet-bg': '{color.bp-surface}',
    'tspage-ink': '{color.bp-ink}',
    'tspage-muted': '{color.bp-muted}',
    'tspage-fs': '{fs.body}',
    'hpage-gap': '{space.6}',
    'hpage-max-w': 1240,
    'hpage-sheet-pad': '{space.8}',
    'hpage-sheet-pad-phone': '{space.4}',
    'hpage-sheet-line': '{line-rule}',
    'hpage-sheet-bg': '{color.bp-surface}',
    'hpage-ink': '{color.bp-ink}',
    'hpage-muted': '{color.bp-muted}',
    'hpage-bad': '{color.bp-signal}',
    'hpage-fs': '{fs.table}',
    'hpage-cell-pad': '{space.3} {space.4}',
    'hpage-cell-fw': '{fw.body}',
    'hpage-divider': '{hair}',
    'hpage-head-rule': '{line-rule}',
    'hpage-head-fs': '{fs.caption}',
    'hpage-head-fw': '{fw.strong}',
    'hpage-bad-fw': '{fw.strong}',
    'hpage-locked-fw': '{fw.strong}',
    'hpage-lock-gap': '{space.3}',
    'hpage-list-indent': '{space.5}',
    'hpage-now-gap': '{space.2}',
    // The board (§8). Locked by test/system.test.ts so changing one is always a choice; the board's
    // components read them from step 4 on.
    'board-names-w': 150,
    'board-scale-h': 36,
    'lane-h': 80,
    'lane-stack-h': 68,
    'lane-h-compact': 36,
    'visit-h': 56,
    'visit-inset': 12,
    'visit-h-compact': 26, // extension: one line in a 36 px lane

    // ── Round 2: the board's own components (packages/ui-web ROUND-2.md). A pen is per technician, so
    // a component that draws one reads --<prefix>-pen, which its element sets to one of its own
    // --<prefix>-pen-N (a var() inside a token would be fixed where the token is defined, on :root). ──
    // Board: one ruled frame, the names held still while the track scrolls sideways
    'board-track-min': 720,
    'board-frame': '{line-rule}',
    'board-bg': '{color.bp-surface}',
    // a phone (QA-765): the names column narrow, each lane's short name, so the lanes have the width
    'board-names-w-phone': 112,
    'board-focus': '{focus-line}',
    'board-focus-offset': 'calc(-1 * {focus-offset})',
    // PenKey: a technician's stroke beside their name
    'penkey-w': 14,
    'penkey-line': '{w.mark}',
    'penkey-gap': '{space.2}',
    'penkey-radius': '{radius.r-hair}',
    'penkey-pen': '{color.bp-muted}',
    ...pens('penkey'),
    // ── primitives.tsx's Section and Figures in their own folders (BL-189), base.css's drawing moved ──
    // Figures: a title block's figures, a rule above and below, hairlines between (.flw-figs, .flw-fig)
    'figures-muted': '{color.bp-muted}',
    'figures-min': 140,
    'figures-lh': '{lh.body}',
    'figures-rule': '{line-rule}',
    'figures-divider': '{hair}',
    'figures-gap': '{space.1}',
    'figures-pad': '{space.5} {space.6}',
    'figures-label-fs': '{fs.caption}',
    'figures-label-fw': '{fw.strong}',
    'figures-value-fs': '{fs.figure}',
    'figures-value-fw': '{fw.figure}',
    'figures-value-lh': '{lh.tight}',
    'figures-sub-fs': '{fs.label}',
    'figures-underline-offset': 3,
    // Section: a ruled section of a page, never a box (.flw-sec)
    'section-muted': '{color.bp-muted}',
    'section-gap': '{gap-section}',
    'section-pad': '{pad-section}',
    'section-rule': '{line-rule}',
    'section-h-gap': '{space.2}',
    'section-h-end': '{space.5}',
    'section-h-fs': '{fs.body-lg}',
    'section-h-fw': '{fw.strong}',
    'section-h-lh': '{lh.tight}',
    'section-cnt-fs': '{fs.label}',
    // where it stands (BL-189): StateMark and its word at the end of the name's line
    'section-ink': '{color.bp-ink}',
    'section-state-gap': '{space.2}',
    'section-state-fs': '{fs.label}',
    'section-state-fw': '{fw.strong}',
    // ── Schedule's Team manager (BL-189: Who's working and Time off moved onto the patterns) ──
    // TeamList: who's working today, one technician a row on hairlines (base.css's .flw-rows, .flw-team)
    'teamlist-ink': '{color.bp-ink}',
    'teamlist-muted': '{color.bp-muted}',
    'teamlist-bad': '{color.bp-signal}',
    'teamlist-fs': '{fs.body}',
    'teamlist-lh': '{lh.body}',
    'teamlist-row-gap': '{space.1} {space.5}',
    'teamlist-row-pad': '{pad-row} 0',
    'teamlist-rule': '{hair}',
    'teamlist-on-pad': '{space.3}',
    'teamlist-on-mark': '{mark}',
    'teamlist-name-fw': '{fw.strong}',
    'teamlist-word-gap': '{space.2}',
    'teamlist-word-fs': '{fs.label}',
    'teamlist-word-fw': '{fw.strong}',
    'teamlist-wait-offset': '{space.1}',
    'teamlist-sub-fs': '{fs.body-sm}',
    'teamlist-pick-fw': '{fw.strong}',
    'teamlist-underline-offset': 3,
    'teamlist-focus': '{focus-line}',
    'teamlist-focus-offset': '{focus-offset}',
    'teamlist-empty-pad': '{space.7} 0',
    'teamlist-empty-rule': '{dash}',
    // TechStops: one technician's day, stop by stop (base.css's .flw-techday, .flw-stops)
    'techstops-ink': '{color.bp-ink}',
    'techstops-muted': '{color.bp-muted}',
    'techstops-bad': '{color.bp-signal}',
    'techstops-fs': '{fs.body}',
    'techstops-lh': '{lh.body}',
    'techstops-who-gap': '{space.3}',
    'techstops-who-end': '{space.1}',
    'techstops-name-fw': '{fw.strong}',
    'techstops-sub-fs': '{fs.body-sm}',
    'techstops-stops-top': '{space.4}',
    'techstops-time-w': 74,
    'techstops-node': 14,
    'techstops-stop-gap': '{space.4}',
    'techstops-stop-pad': '{pad-row} 0',
    'techstops-rule': '{hair}',
    'techstops-time-fw': '{fw.strong}',
    'techstops-end-fs': '{fs.caption}',
    'techstops-end-fw': '{fw.body}',
    'techstops-node-top': '{space.1}',
    'techstops-node-fill': '{color.bp-surface}',
    'techstops-node-fill-off': '{color.bp-grid}',
    'techstops-node-line': '{w.rule}',
    'techstops-node-dash': '2 2',
    'techstops-word-fs': '{fs.label}',
    'techstops-word-fw': '{fw.strong}',
    'techstops-wait-offset': '{space.1}',
    'techstops-empty-pad': '{space.7} 0',
    'techstops-empty-rule': '{dash}',
    // TimeOffList: time off, booked and cancelled, one row on hairlines (base.css's .flw-rows, .flw-struck)
    'timeoff-ink': '{color.bp-ink}',
    'timeoff-muted': '{color.bp-muted}',
    'timeoff-fs': '{fs.body}',
    'timeoff-lh': '{lh.body}',
    'timeoff-row-gap': '{space.1} {space.5}',
    'timeoff-row-pad': '{pad-row} 0',
    'timeoff-rule': '{hair}',
    'timeoff-name-fw': '{fw.strong}',
    'timeoff-sub-fs': '{fs.body-sm}',
    'timeoff-word-fs': '{fs.label}',
    'timeoff-word-fw': '{fw.strong}',
    'timeoff-cancel-fw': '{fw.strong}',
    'timeoff-underline-offset': 3,
    'timeoff-focus': '{focus-line}',
    'timeoff-focus-offset': '{focus-offset}',
    'timeoff-empty-pad': '{space.7} 0',
    'timeoff-empty-rule': '{dash}',
    // NoteList: the notifications by group (Inbox › Notifications; base.css's .flw-notes, as drawn)
    'notelist-ink': '{color.bp-ink}',
    'notelist-muted': '{color.bp-muted}',
    'notelist-bad': '{color.bp-signal}',
    'notelist-surface': '{color.bp-surface}',
    'notelist-fs': '{fs.body}',
    'notelist-lh': '{lh.body}',
    'notelist-rule': '{hair}',
    'notelist-group-gap': '{space.5}',
    'notelist-group-pad': '{space.4}',
    'notelist-head-gap': '{space.2}',
    'notelist-head-fw': '{fw.strong}',
    'notelist-head-lh': '{lh.tight}',
    'notelist-row-gap': '{space.1} {space.5}',
    'notelist-row-pad': '{space.4} 0',
    'notelist-mark-size': 10,
    'notelist-mark-gap': '{space.4}',
    'notelist-mark-top': '{space.1}',
    'notelist-mark-stroke': '{w.rule}',
    'notelist-sub-fs': '{fs.body-sm}',
    'notelist-title-fs': '{fs.body-lg}',
    'notelist-title-fw': '{fw.body}',
    'notelist-unread-fw': '{fw.strong}',
    'notelist-underline-offset': 3,
    'notelist-focus': '{focus-line}',
    'notelist-focus-offset': '{focus-offset}',
    'notelist-empty-pad': '{space.7} 0',
    'notelist-empty-rule': '{dash}',
    // RowList: things one a row on hairlines, the sample's .item row (kit.css; base.css's .flw-rows):
    // the company's holidays, the books' months (BL-189 consolidation)
    'rowlist-ink': '{color.bp-ink}',
    'rowlist-muted': '{color.bp-muted}',
    'rowlist-bad': '{color.bp-signal}',
    'rowlist-fs': '{fs.body}',
    'rowlist-lh': '{lh.body}',
    'rowlist-row-gap': '{space.1} {space.5}',
    'rowlist-row-pad': '{pad-row} 0',
    'rowlist-rule': '{hair}',
    'rowlist-on-pad': '{space.3}',
    'rowlist-on-mark': '{mark}',
    'rowlist-name-fs': '{fs.body-lg}',
    'rowlist-name-fw': '{fw.strong}',
    'rowlist-underline-offset': 3,
    'rowlist-focus': '{focus-line}',
    'rowlist-focus-offset': '{focus-offset}',
    'rowlist-word-gap': '{space.2}',
    'rowlist-word-fs': '{fs.label}',
    'rowlist-word-fw': '{fw.strong}',
    'rowlist-wait-offset': '{space.1}',
    'rowlist-sub-fs': '{fs.body-sm}',
    'rowlist-more-top': '{space.4}',
    'rowlist-note-top': '{space.4}',
    'rowlist-empty-pad': '{space.7} 0',
    'rowlist-empty-rule': '{dash}',
    // its own title and the line under it, and its one way in (Inbox's views, N1)
    'rowlist-title-fs': '{fs.name}',
    'rowlist-title-fw': '{fw.strong}',
    'rowlist-title-lh': '{lh.tight}',
    'rowlist-act-gap': '{space.3} {space.6}',
    'rowlist-act-top': '{space.6}',
    // a row's actions on its right (To schedule): the column, the name's line with its end, two at rest,
    // More under the first; a bulleted line
    'rowlist-acts-gap': '{space.6}',
    'rowlist-head-gap': '{space.1} {space.6}',
    'rowlist-acts-row-gap': '{space.6}',
    'rowlist-acts-more-gap': '{space.2}',
    'rowlist-items-margin': '{space.5} 0',
    'rowlist-items-indent': '{space.10}',
    // a row's bar (from call to invoice): the name's column between these
    'rowlist-bar-name-min': '6em',
    'rowlist-bar-name-max': '10em',
    // ── Schedule's Book a visit (BL-189) ──
    // WindowWeek: a week of arrival windows (book.css and blueprint/schedule.css's .bk-cal, .bk-week)
    'wweek-ink': '{color.bp-ink}',
    'wweek-muted': '{color.bp-muted}',
    'wweek-fs': '{fs.body}',
    'wweek-lh': '{lh.body}',
    'wweek-gap': '{space.4}',
    'wweek-end': '{space.3}',
    'wweek-head-gap': '{space.5}',
    'wweek-range-fs': '{fs.body-lg}',
    'wweek-range-fw': '{fw.total}',
    'wweek-say-fs': '{fs.body-sm}',
    'wweek-say-lh': '{lh.list}',
    'wweek-head-w': 46,
    'wweek-hair': '{hair}',
    'wweek-day-pad': '{space.1}',
    'wweek-day-fs': '{fs.caption}',
    'wweek-day-lh': '{lh.snug}',
    'wweek-day-name-fs': '{fs.table}',
    'wweek-day-name-fw': '{fw.total}',
    'wweek-today-thick': 2,
    'wweek-today-offset': 3,
    'wweek-window-pad': '{space.1} {space.2}',
    'wweek-window-pad-narrow': '{space.0} {space.1}',
    'wweek-window-fs': '{fs.table}',
    'wweek-window-fw': '{fw.strong}',
    'wweek-part-fw': '{fw.body}',
    'wweek-cell-h': '{size.tap}',
    'wweek-cell-pad': '0 {space.0}',
    'wweek-cell-fs': '{fs.body-sm}',
    'wweek-cell-fw': '{fw.strong}',
    'wweek-cell-lh': '{lh.tight}',
    'wweek-quiet-fw': '{fw.body}',
    'wweek-focus': '{w.focus} solid {color.bp-ink}',
    'wweek-full-bg': '{hatch-muted}',
    'wweek-past-bg': '{color.bp-bg}',
    'wweek-chosen-bg': '{color.bp-ink}',
    'wweek-chosen-ink': '{color.bp-on-ink}',
    'wweek-jump-w': 280,
    'wweek-closed-mark': '{w.rule} dashed {color.bp-muted}',
    'wweek-closed-w': '{space.6}',
    // MatchList: what a search box's words match, framed under it, a tap tall each
    'matchlist-pad': '{space.1}',
    'matchlist-border': '{line-rule}',
    'matchlist-radius': '{radius.r-hair}',
    'matchlist-bg': '{color.bp-surface}',
    'matchlist-row-h': '{control-h}',
    'matchlist-row-pad': '{space.2} {space.3}',
    'matchlist-ink': '{color.bp-ink}',
    'matchlist-fw': '{fw.strong}',
    'matchlist-line': '{w.hair}',
    'matchlist-line-hover': '{w.focus}',
    'matchlist-line-offset': '{space.1}',
    'matchlist-focus': '{focus-line}',
    'matchlist-focus-offset': '{focus-offset}',
    // TimeScale: the hours over the lanes, a tick each hour, now in red
    'scale-h': '{board-scale-h}',
    'scale-rule': '{line-rule}',
    'scale-bg': '{color.bp-surface}',
    'scale-ink': '{color.bp-muted}',
    'scale-fs': '{fs.caption}',
    'scale-fw': '{fw.strong}',
    'scale-lh': '{lh.tight}',
    'scale-tick-h': 8,
    'scale-tick-w': '{w.rule}',
    'scale-tick-ink': '{color.bp-stroke}',
    'scale-label-bottom': '{space.4}',
    'scale-corner-pad': '{space.7}',
    // the names column is never narrower than the board's names (it fits its names when grouped)
    'scale-corner-min-w': '{board-names-w}',
    'scale-corner-pad-phone': '{space.3}',
    'scale-now-ink': '{color.bp-signal}',
    'scale-now-top': '{space.1}',
    'scale-now-pad-x': '{space.1}',
    // NowLine: the red line at now, through every lane
    'now-w': '{w.rule}',
    'now-ink': '{color.bp-signal}',
    // VisitBlock: 56 px, three lines that never wrap; status drawn (DESIGN.md §2)
    'visit-pad-y': 5,
    'visit-pad-x': 7,
    'visit-pad-x-narrow': '{w.focus}',
    // a narrow problem visit drops its words and lets its name take two lines
    'visit-who-lines-narrow': '2', // a count, not a length
    // and no taller than those two lines, so a third never shows its top
    'visit-who-max-h-narrow': '2lh',
    'visit-gap-compact': '{space.2}',
    'visit-line': '{w.rule}',
    'visit-ghost-line': '{w.mark}',
    'visit-radius': '{radius.r-hair}',
    'visit-bg': '{color.bp-surface}',
    'visit-ink': '{color.bp-ink}',
    'visit-muted': '{color.bp-muted}',
    'visit-bad': '{color.bp-signal}',
    'visit-on-pen': '{color.bp-on-pen}',
    'visit-pen': '{color.bp-ink}',
    ...pens('visit'),
    // a booked visit is filled with its pen's wash, nobody's with the sheet; a problem drops the wash
    // so its red words keep their contrast (Amir, 26 Oct: option D)
    'visit-wash': '{color.bp-surface}',
    ...pens('visit', 'wash'),
    'visit-late-bg': '{color.bp-surface}',
    // extension: the leading stripe in the full pen, and the start padding that clears it
    'visit-edge': 4,
    'visit-pad-x-pen': 'calc({visit-pad-x} + {visit-edge})',
    'visit-pad-x-narrow-pen': 'calc({visit-pad-x-narrow} + {visit-edge})',
    'visit-fs': '{fs.caption}',
    'visit-time-fs': '{fs.body-sm}',
    'visit-time-fw': '{fw.strong}',
    'visit-state-fw': '{fw.strong}',
    'visit-lh': '{lh.snug}',
    'visit-hatch-line': 1,
    'visit-hatch-gap': 8,
    'visit-late-ring': '0 0 0 {w.focus} {color.bp-signal}',
    'visit-pressed-ring': 'inset 0 0 0 {w.rule} currentColor',
    'visit-late-pressed-ring': '0 0 0 {w.focus} {color.bp-signal}, inset 0 0 0 {w.rule} currentColor',
    'visit-selected': '{line-strong}',
    'visit-selected-offset': 2,
    'visit-focus': '{focus-line}',
    'visit-focus-offset': '{focus-offset}',
    'visit-underline': '{w.hair}',
    'visit-underline-offset': 2,
    // the Now and Next tags before the window (BL-070)
    'visit-tag-gap': '{space.1}',
    'visit-tag-pad-x': '{space.1}',
    'visit-tag-border': '{w.hair} solid currentColor',
    'visit-tag-radius': '{radius.r-hair}',
    'visit-tag-fs': '{fs.caption}',
    'visit-tag-fw': '{fw.strong}',
    // HoverCard: what a block doesn't show, on the pop layer
    'hovercard-w': 320,
    'hovercard-max-w': '100vw',
    'hovercard-offset': '{space.2}',
    // BoardLane: a technician's head and track; 80 for one row of visits, 68 for each extra stack
    'lane-bg': '{color.bp-surface}',
    'lane-divider': '{hair}',
    'lane-row-h': '{lane-h}',
    'lane-row-stack-h': '{lane-stack-h}',
    'lane-row-h-compact': '{lane-h-compact}',
    'lane-row-stack-h-compact': 30,
    'lane-visit-inset': '{visit-inset}',
    'lane-visit-inset-compact': 5,
    'lane-visit-h': '{visit-h}',
    'lane-visit-h-compact': '{visit-h-compact}',
    'lane-slot-gap': 1,
    'lane-head-pad': '{space.3} {space.4} {space.3} {space.7}',
    // grouped or arranging (Amir, 26 Oct: "Brook Po…", "*To be Dis…" were cut) the names column fits
    // its longest name (Board names="fit"); a head is never wider than this, its other lines never widen it
    'lane-head-max-w': 240,
    'lane-head-max-w-touch': 220,
    'lane-head-w-phone': '{board-names-w-phone}',
    'lane-head-pad-phone': '{space.3} {space.3} {space.3} {space.4}',
    // the name and the line under it are both buttons: their centres 24 apart or more (WCAG 2.2 target size)
    'lane-target-gap': 6,
    'lane-head-pad-compact': '0 {space.4} 0 {space.7}',
    'lane-head-gap': 2,
    'lane-name-fs': '{fs.body-lg}',
    'lane-name-fw': '{fw.strong}',
    'lane-name-ink': '{color.bp-ink}',
    'lane-line-fs': '{fs.label}',
    'lane-line-fw': '{fw.body}',
    'lane-line-ink': '{color.bp-muted}',
    'lane-lh': '{lh.snug}',
    'lane-bad': '{color.bp-signal}',
    'lane-bad-fw': '{fw.strong}',
    'lane-underline': '{w.hair}',
    'lane-underline-offset': 3,
    'lane-focus': '{focus-line}',
    'lane-focus-offset': '{focus-offset}',
    /** The pick button covers the track: its focus line sits inside it. */
    'lane-pick-offset': 'calc(-1 * {focus-offset})',
    'lane-hour': '{color.bp-rule}',
    'lane-quarter': '{color.bp-grid}',
    'lane-grid-w': '{w.hair}',
    'lane-off-bg': '{hatch-muted}',
    'lane-off-edge-w': '{w.rule}',
    'lane-off-ink': '{color.bp-muted}',
    'lane-off-fs': '{fs.caption}',
    'lane-off-pad': '{space.1} {space.2}',
    'lane-pipe-w': '{w.rule}',
    'lane-pipe-dash': 4,
    'lane-pipe-period': 7,
    'lane-pipe-ink': '{color.bp-muted}',
    'lane-pipe-bad': '{color.bp-signal}',
    'lane-pipe-cap-h': 7,
    'lane-pipe-fs': '{fs.caption}',
    'lane-pipe-fw': '{fw.strong}',
    'lane-pipe-bg': '{color.bp-surface}',
    'lane-pipe-pad-x': '{space.1}',
    'lane-over': '{dash-signal}',
    'lane-target': '{mark}',
    'lane-target-underline': '{w.mark}',
    // TechDayCard: a technician's day so far, live, on top of their menu (pages/board-tech-menu.md)
    'techday-gap': '{space.3}',
    'techday-group-gap': '{space.1}',
    'techday-head-gap': '{space.4}',
    'techday-name-fs': '{fs.body}',
    'techday-name-fw': '{fw.strong}',
    'techday-name-ink': '{color.bp-ink}',
    'techday-asof-fs': '{fs.caption}',
    'techday-asof-ink': '{color.bp-muted}',
    'techday-now-fs': '{fs.body-sm}',
    'techday-now-fw': '{fw.strong}',
    'techday-fs': '{fs.body-sm}',
    'techday-fw': '{fw.body}',
    'techday-lh': '{lh.snug}',
    'techday-ink': '{color.bp-ink}',
    'techday-muted': '{color.bp-muted}',
    'techday-bad': '{color.bp-signal}',
    'techday-bad-fw': '{fw.strong}',
    'techday-bad-mark': '{line-signal}',
    'techday-bad-pad': '{space.3}',
    'techday-underline': '{w.hair}',
    'techday-underline-offset': 3,
    'techday-underline-hover': '{w.focus}',
    'techday-focus': '{focus-line}',
    'techday-focus-offset': '{focus-offset}',
    // DivisionGroup: a ruled heading row over a division's lanes; folded, a thin bar per technician
    'division-rule': '{line-rule}',
    'division-divider': '{hair}',
    'division-bg': '{color.bp-surface}',
    'division-pad': '{space.3} {space.4} {space.3} {space.7}',
    'division-gap': 2,
    'division-fs': '{fs.body}',
    'division-fw': '{fw.strong}',
    'division-lh': '{lh.snug}',
    'division-ink': '{color.bp-ink}',
    'division-sub-fs': '{fs.caption}',
    'division-sub-ink': '{color.bp-muted}',
    'division-bad': '{color.bp-signal}',
    'division-bad-fw': '{fw.strong}',
    'division-glyph': '{size.icon}',
    'division-stroke': '{icon-stroke}',
    'division-toggle-gap': '{space.2}',
    'division-underline': '{w.hair}',
    'division-underline-offset': 3,
    'division-focus': '{focus-line}',
    'division-focus-offset': '{focus-offset}',
    'division-mini-h': 5,
    'division-mini-gap': '{space.1}',
    'division-mini-pad': '{space.4}',
    'division-mini-line': '{w.hair}',
    'division-mini-done': '{hatch-muted}',
    'division-pen': '{color.bp-ink}',
    'division-hour': '{color.bp-rule}',
    'division-grid-w': '{w.hair}',
    ...pens('division'),
    // BoardBand: the date and the day in words, left; ‹ Today ›, rows and Book a visit, right. No
    // padding of its own (Amir, 26 Oct: too much above the date, and between it and the board): the
    // sheet's padding is above it, as above every page's title, and the page's gap under it
    'band-pad': 0,
    'band-gap': '{space.5}',
    'band-controls-gap': '{space.3}',
    'band-title-fs': '{fs.h2}',
    'band-title-fw': '{fw.strong}',
    'band-title-lh': '{lh.tight}',
    'band-line-gap': '{space.1}',
    'band-line-fs': '{fs.body}',
    'band-line-lh': '{lh.list}',
    'band-line-ink': '{color.bp-muted}',
    'band-ink': '{color.bp-ink}',
    'band-bad': '{color.bp-signal}',
    'band-bad-fw': '{fw.strong}',
    // BoardCorner: Group and Filter over the names (DESIGN.md §8), quiet small controls (option A,
    // 26 Oct): the band's gap between Group ▾ and Filter, as two separate tools
    'corner-gap': '{space.3}',
    // at touch widths: the ▾'s and Filter's 44 px press areas side by side, never overlapping
    'corner-gap-touch': 'calc({size.tap} - {iconbtn-caret-w-sm} + ({size.tap} - {size.sm}) / 2)',
    // the glyph, not its button's box, on the names' left edge
    'corner-inset': 'calc(({size.icon} - {size.sm}) / 2)',
    // SidePanel: the visit on the panel layer, at the board's right
    // where it sits: at the right of the page, from the top (phase B sets it under the app's bar)
    // round 2: "‹ Back to …" above a detail's first line
    'panel-before-gap': '{space.3}',
    'panel-before-fs': '{fs.label}',
    'panel-top': '{space.6}',
    'panel-inset': '{space.6}',
    'panel-max-h': 'calc(100vh - 2 * {space.6})',
    'panel-gap': '{space.6}',
    'panel-title-fs': '{fs.name}',
    'panel-title-fw': '{fw.strong}',
    'panel-title-lh': '{lh.snug}',
    'panel-sub-fs': '{fs.table}',
    'panel-sub-ink': '{color.bp-muted}',
    'panel-sub-gap': 2,
    'panel-bad': '{color.bp-signal}',
    'panel-bad-fs': '{fs.body-sm}',
    'panel-bad-fw': '{fw.strong}',
    'panel-actions-gap': '{space.5}',
    'panel-links-gap': '{space.3} {space.5}',
    'panel-link-fs': '{fs.table}',
    // MapStrip: the day's routes under the board
    'mapstrip-pad': '{space.6} 0',
    'mapstrip-legend-w': 260,
    // the map is drawn no taller than this; a wider drawing is scaled to fit and centred, never cut
    'mapstrip-map-max-h': 320,
    'mapstrip-gap': '{space.8}',
    'mapstrip-label-fs': '{fs.label}',
    'mapstrip-label-fw': '{fw.strong}',
    'mapstrip-label-ink': '{color.bp-muted}',
    'mapstrip-row-pad': '{space.3}',
    'mapstrip-divider': '{hair}',
    'mapstrip-name-fs': '{fs.body}',
    'mapstrip-name-fw': '{fw.strong}',
    'mapstrip-line-fs': '{fs.label}',
    'mapstrip-line-ink': '{color.bp-muted}',
    'mapstrip-lh': '{lh.snug}',
    'mapstrip-bad': '{color.bp-signal}',
    'mapstrip-empty-fs': '{fs.body}',
    // StatusBar: the day's states with counts, filtering the job table
    'statusbar-rule': '{line-rule}',
    'statusbar-divider': '{hair}',
    'statusbar-group-rule': '{line-rule}',
    'statusbar-pad': '{space.3} {space.4}',
    'statusbar-gap': '{space.2}',
    'statusbar-fs': '{fs.table}',
    'statusbar-fw': '{fw.body}',
    'statusbar-lh': '{lh.tight}',
    'statusbar-ink': '{color.bp-muted}',
    'statusbar-on-ink': '{color.bp-ink}',
    'statusbar-on-fw': '{fw.strong}',
    'statusbar-mark-w': '{w.strong}',
    'statusbar-count-fs': '{fs.body}',
    'statusbar-count-fw': '{fw.strong}',
    'statusbar-count-ink': '{color.bp-ink}',
    'statusbar-urgent-ink': '{color.bp-ink}',
    'statusbar-bg': '{color.bp-surface}',
    'statusbar-underline': '{w.hair}',
    'statusbar-underline-offset': 3,
    'statusbar-focus': '{focus-line}',
    'statusbar-focus-offset': 'calc(-1 * {focus-offset})',
    // at phone widths, each tab a touch target
    'statusbar-tab-h-touch': '{control-h-touch}',
    // a group's scope before its tabs (QA-768): "Today’s visits", "Any day"
    'statusbar-scope-pad': '{space.3} {space.2} {space.3} {space.4}',
    'statusbar-scope-ink': '{color.bp-muted}',
    'statusbar-scope-fs': '{fs.caption}',
    'statusbar-scope-fw': '{fw.strong}',
    // truck stock (BL-203): the Stock page's places, each with a second line (who has it)
    'statusbar-sub-gap': 2,
    'statusbar-sub-ink': '{color.bp-muted}',
    'statusbar-sub-fs': '{fs.caption}',
    'statusbar-sub-fw': '{fw.body}',
    // JobTable: one sortable table the status bar filters
    'jobtable-head-fs': '{fs.caption}',
    'jobtable-head-fw': '{fw.strong}',
    'jobtable-head-ink': '{color.bp-muted}',
    'jobtable-head-rule': '{line-rule}',
    'jobtable-divider': '{hair}',
    'jobtable-pad': '{space.3} {space.4}',
    'jobtable-fs': '{fs.table}',
    'jobtable-lh': '{lh.list}',
    'jobtable-ink': '{color.bp-ink}',
    'jobtable-muted': '{color.bp-muted}',
    'jobtable-strong-fw': '{fw.strong}',
    'jobtable-bad': '{color.bp-signal}',
    'jobtable-bg': '{color.bp-surface}',
    'jobtable-pressed-bg': '{color.bp-grid}',
    'jobtable-glyph': '{size.icon}',
    'jobtable-stroke': '{icon-stroke}',
    'jobtable-sort-gap': '{space.1}',
    'jobtable-underline': '{w.hair}',
    'jobtable-underline-offset': 3,
    'jobtable-focus': '{focus-line}',
    'jobtable-focus-offset': 'calc(-1 * {focus-offset})',
    'jobtable-cell-max-w': 220,
    'jobtable-wide-max-w': 340,
    'jobtable-empty-pad': '{space.8} {space.4}',
    // one of several tables (Paused, by reason): its title over it
    'jobtable-title-pad': '{space.3} {space.4} {space.2}',
    'jobtable-title-fs': '{fs.body}',
    // at phone widths, each row a touch target
    'jobtable-row-h-touch': '{control-h-touch}',
    // scrolled sideways (a phone): how far in an edge it goes on past fades out (QA-841)
    'jobtable-fade': '{space.8}',

    // ── The board's grouping (concept A, Amir 26 Oct: pages/board-tools): Group and its ▾ in one frame,
    // Filter's count, a radio mark in a menu, and arranging divisions on the board itself. ──
    // IconButton: the count over its corner, its name on hover (HoverCard's frame), joined to a ▾
    'iconbtn-size-touch': '{control-h-touch}',
    'iconbtn-caret-w': 18,
    'iconbtn-join-radius': 0,
    'iconbtn-count-inset': 'calc(-1 * {space.3})',
    'iconbtn-count-min': 16,
    'iconbtn-count-pad': '0 {space.1}',
    'iconbtn-count-border': '{w.rule} solid {color.bp-ink}',
    'iconbtn-count-bg': '{color.bp-surface}',
    'iconbtn-count-ink': '{color.bp-ink}',
    'iconbtn-count-fs': '{fs.caption}', // the scale's smallest, in a 16 px box
    'iconbtn-count-fw': '{fw.total}',
    'iconbtn-count-lh': '{lh.tight}',
    'iconbtn-count-radius': '{radius.r-hair}',
    'iconbtn-tip-max-w': 260,
    'iconbtn-tip-offset': '{space.2}',
    'iconbtn-tip-fs': '{fs.label}',
    'iconbtn-tip-lh': '{lh.snug}',
    'iconbtn-tip-fw': '{fw.strong}',
    'iconbtn-tip-ink': '{color.bp-ink}',
    'iconbtn-tip-note-fw': '{fw.body}',
    'iconbtn-tip-note-ink': '{color.bp-muted}',
    // Menu: a radio item's mark (●) before its words
    'menu-mark-w': '{size.icon}',
    'menu-mark-fw': '{fw.total}',
    'menu-note-gap': '{space.1}',
    'menu-note-ink': '{color.bp-muted}',
    'menu-note-fw': '{fw.body}',
    'menu-note-title-ink': '{color.bp-ink}',
    // DivisionGroup: open, a slim row with its name; arranging, ⋮⋮ and the name as a ruled field
    'division-pad-slim': '{space.2} {space.4} {space.2} {space.7}',
    'division-pad-edit': '{space.2} {space.4} {space.2} {space.2}',
    'division-input-rule': '{line-rule}',
    'division-input-pad': '{division-gap} 0',
    // a phone's division name field: within what shows of the board at 360 (QA-767)
    'division-input-max-phone': 240,
    'division-grip-w': 22,
    'division-grip-h': 28,
    'division-grip-ink': '{color.bp-muted}',
    'division-grip-hover': '{color.bp-ink}',
    'division-grip-glyph': '{size.icon}',
    'division-grip-stroke': '{w.focus}',
    'division-target-bg': '{color.bp-grid}',
    'division-target-ring': 'inset 0 0 0 {w.mark} {color.bp-ink}',
    'division-touch': '{control-h-touch}',
    // BoardLane: ⋮⋮ before the name while arranging; moved for today, "Usually in …" in ink
    // indented under its division's ⋮⋮ (the lanes' own start, space.7) and drawn smaller than it (12
    // to the heading's 16), so a person reads as inside their division (Amir, 26 Oct)
    'lane-head-pad-grip': '{space.3} {space.4} {space.3} {space.7}',
    // at touch widths ⋮⋮ is 44 wide, so it starts nearer the edge; its glyph still sits right of the
    // division's
    'lane-head-pad-grip-touch': '{space.3} {space.4} {space.3} {space.5}',
    'lane-grip-gap': '{space.1}',
    'lane-grip-w': 14,
    'lane-grip-h': 28,
    'lane-grip-ink': '{color.bp-muted}',
    'lane-grip-hover': '{color.bp-ink}',
    'lane-grip-glyph': 12,
    'lane-grip-stroke': '{w.focus}',
    'lane-grip-focus': '{focus-line}',
    'lane-grip-touch': '{control-h-touch}',
    'lane-moved-ink': '{color.bp-ink}',
    'lane-moved-fw': '{fw.strong}',
    // BoardBand while arranging: a strong rule over it says the board is in a mode
    'band-edit-frame': '{wait-area}',
    'band-edit-bg': '{wait-area-bg}',
    'band-edit-top': '{space.3}',
    'band-edit-line-ink': '{color.bp-ink}',
    'band-edit-line-max': '72ch',
    'band-edit-focus': '{focus-line}',
    'band-edit-focus-offset': '{focus-offset}',
    // the mode's own room under its rule (the band has none of its own)
    'band-edit-pad': '{wait-area-pad}',
    // Set up divisions, the first time: "Not set up yet", the state word for waiting (DESIGN.md §3a: a
    // label is its word, a dashed underline for waiting; Setup's .chip.warn), never red
    'band-state-fs': '{fs.label}',
    'band-state-fw': '{fw.strong}',
    'band-state-ink': '{color.bp-ink}',
    'band-state-underline': 'underline dashed',
    'band-state-offset': '{space.1}',

    // ── Dispatch polish 3 (Amir, 26 Oct; hub BL-158) ──
    // IconButton, option A of the corner (pages/board-corner): size="sm" is §8's small control (26 px),
    // quiet has no frame until it's on (a hairline ring on hover, the ring Close had before round 2);
    // with size="sm" a count is a word inside it ("⏷ 2"), and at touch widths its press area is 44 px,
    // an invisible ring
    'iconbtn-size-sm': '{control-h-sm}',
    'iconbtn-caret-w-sm': 16,
    'iconbtn-pad-x-sm': '{space.2}',
    'iconbtn-count-gap': '{space.1}',
    'iconbtn-quiet-ring': 'inset 0 0 0 {w.hair} {color.bp-rule}',
    // the 44 px press area round a 26 px drawing, by how far it runs out: evenly, or (a joined pair) all
    // to Group's start side and all to the ▾'s end side, so the two never overlap
    'iconbtn-hit-inset': 'calc(({size.sm} - {size.tap}) / 2)',
    'iconbtn-hit-inset-join': 'calc({size.sm} - {size.tap})',
    'iconbtn-hit-inset-caret': 'calc({iconbtn-caret-w-sm} - {size.tap})',
    // the ▾ beside Group while Group is on: an ink edge round it, so the pair reads as one split button
    'iconbtn-join-on': '{color.bp-ink}',
    // DivisionGroup while arranging: a division dragged by its ⋮⋮ lands before the heading with an ink
    // mark over it
    'division-slot-mark': 'inset 0 {w.mark} 0 0 {color.bp-ink}',
    // ---- job page round 2 (Amir 26 Oct; pages/job-page-r2): the job record's generic parts ----
    // Skeleton: grey bars where words will be, while the job loads (point 1: never "A job")
    'skel-w-short': 96,
    'skel-w-mid': 180,
    'skel-w-long': 320,
    'skel-w-full': '100%',
    'skel-h-line': 10,
    'skel-line-pad': '{space.1}',
    'skel-radius': '{radius.r-hair}',
    'skel-fill': '{color.bp-grid}',
    'skel-h-title': 22,
    'skel-title-pad': '{space.2}',
    'skel-h-name': 14,
    'skel-h-small': 8,
    'skel-h-row': 44,
    'skel-rule': '{hair}',
    'skel-row-fill': '{color.bp-surface}',
    'skel-thumb-w': 96,
    'skel-thumb-h': 72,
    'skel-button-w': 132,
    'skel-button-h': '{control-h}',
    'skel-gap': '{space.3}',
    'skel-sr-size': 1,
    // StateLine: where the job stands, and the next thing, in two lines under the heading (point 5)
    'stline-gap': '{space.5}',
    'stline-row-gap': '{space.1}',
    'stline-ink': '{color.bp-ink}',
    'stline-word-fs': '{fs.name}',
    'stline-word-fw': '{fw.strong}',
    'stline-lh': '{lh.snug}',
    'stline-now-fs': '{fs.body}',
    'stline-muted': '{color.bp-muted}',
    'stline-label-fs': '{fs.label}',
    'stline-label-fw': '{fw.strong}',
    'stline-next-fs': '{fs.body}',
    'stline-bad': '{color.bp-signal}',
    'stline-strong-fw': '{fw.strong}',
    // RecordRow: one ruled row a thing (a visit, a form), the when in History's time column, opens its detail
    'rrow-gap': '{hist-gap}',
    'rrow-pad-y': '{hist-pad}',
    'rrow-kind-w': '{hist-kind-w}',
    'rrow-glyph': '{size.icon}',
    'rrow-glyph-nudge': '{w.focus}',
    'rrow-stroke': '{icon-stroke}',
    'rrow-pad-x': 0,
    'rrow-rule': '{hair}',
    'rrow-ink': '{color.bp-ink}',
    'rrow-fs': '{fs.table}',
    'rrow-lh': '{lh.list}',
    'rrow-when-w': '{hist-time-w}',
    'rrow-hover-bg': '{color.bp-grid}',
    'rrow-underline': '{w.hair}',
    'rrow-underline-offset': 3,
    'rrow-pressed-bg': '{color.bp-grid}',
    'rrow-focus': '{focus-line}',
    'rrow-focus-offset': 'calc(-1 * {focus-offset})',
    'rrow-line-gap': 2,
    'rrow-muted': '{color.bp-muted}',
    'rrow-when-fs': '{fs.body-sm}',
    'rrow-title-fw': '{fw.strong}',
    'rrow-sub-fs': '{fs.body-sm}',
    'rrow-sub-fw': '{fw.body}',
    'rrow-came-line': '{mark}',
    'rrow-came-fs': '{fs.caption}',
    'rrow-h-touch': '{control-h-touch}',
    // opens in place (round 3): drawn as History's rows (HistoryItem) open
    'rrow-chev-w': '{hist-chev-w}',
    'rrow-open-mark': '{hist-open-mark}',
    'rrow-open-pad-end': '{hist-open-pad-end}',
    'rrow-entry-inset': 'calc({rrow-when-w} + {rrow-kind-w} + 2 * {rrow-gap})',
    'rrow-entry-end': 'calc({rrow-chev-w} + {rrow-gap})',
    'rrow-entry-pad': '{hist-entry-pad}',
    'rrow-entry-pad-phone': '{hist-entry-pad-phone}',
    'rrow-entry-inset-phone': '{space.3}',
    // truck stock (BL-203): a row with no time that opens in place steps in past the ink mark
    'rrow-plain-inset': '{space.5}',
    'rrow-static-end': 'calc({rrow-pad-x} + {rrow-chev-w} + {rrow-gap})',
    // Thread: notes and messages, one conversation (point 11), on History's rows
    'thread-gap': '{space.4}',
    'thread-ink': '{color.bp-ink}',
    'thread-fs': '{fs.table}',
    'thread-day-pad': '{feed-day-pad}',
    'thread-day-pad-end': '{feed-day-pad-end}',
    'thread-rule': '{feed-day-rule}',
    'thread-muted': '{color.bp-muted}',
    'thread-day-fs': '{feed-day-fs}',
    'thread-day-fw': '{feed-day-fw}',
    'thread-day-lh': '{feed-day-lh}',
    'thread-bad': '{color.bp-signal}',
    'thread-bad-fw': '{fw.strong}',
    'thread-compose-pad': '{space.3}',
    // EstimateCard: one estimate on the job, its state in words and its options (point 9)
    'estcard-gap': '{space.4}',
    'estcard-pad': '{space.6}',
    'estcard-frame': '{hair}',
    'estcard-radius': '{radius.r-hair}',
    'estcard-bg': '{color.bp-surface}',
    'estcard-ink': '{color.bp-ink}',
    'estcard-fs': '{fs.table}',
    'estcard-lh': '{lh.list}',
    'estcard-frame-draft': '{dash}',
    'estcard-frame-sold': '{line-strong}',
    'estcard-muted': '{color.bp-muted}',
    'estcard-name-fs': '{fs.body-lg}',
    'estcard-strong-fw': '{fw.strong}',
    'estcard-underline': '{w.hair}',
    'estcard-underline-offset': 3,
    'estcard-focus': '{focus-line}',
    'estcard-focus-offset': '{focus-offset}',
    'estcard-num-gap': '{space.3}',
    'estcard-body-fw': '{fw.body}',
    'estcard-word-fs': '{fs.label}',
    'estcard-rule': '{hair}',
    'estcard-letter-w': 20,
    'estcard-opt-pad': '{space.2}',
    'estcard-mark-room': '{space.4}',
    'estcard-chosen-mark': '{mark}',
    'estcard-chosen-pad': '{space.3}',
    'estcard-price-fw': '{fw.figure}',
    'estcard-line-fs': '{fs.body-sm}',
    'estcard-bad': '{color.bp-signal}',
    'estcard-acts-gap': '{space.5}',
    'estcard-list-gap': '{space.5}',
    // ItemPicker: the estimate builder's price book
    'picker-gap': '{space.4}',
    'picker-ink': '{color.bp-ink}',
    'picker-fs': '{fs.table}',
    'picker-lh': '{lh.list}',
    'picker-title-fs': '{fs.body}',
    'picker-strong-fw': '{fw.strong}',
    'picker-muted': '{color.bp-muted}',
    'picker-sub-fs': '{fs.body-sm}',
    'picker-row-gap': '{space.1}',
    'picker-rule': '{line-rule}',
    'picker-row-pad': '{space.3}',
    'picker-divider': '{hair}',
    'picker-body-fw': '{fw.body}',
    'picker-sr-size': 1,
    // the price book (DEC-019): One Find. A picture beside each row, what's before you type (for this job,
    // recent, the shelf) as sections, the option it adds to above the field, and Ask the office.
    'picker-sec-gap': '{space.6}',
    'picker-sec-rule': '{line-rule}',
    'picker-sec-pad': '{space.5}',
    'picker-head-gap': '{space.3}',
    'picker-note-ink': '{color.bp-ink}',
    'picker-none-gap': '{space.2}',
    // OptionEditor: one option of an estimate being built, a column of a document
    'opted-gap': '{space.4}',
    'opted-pad': '{space.5}',
    'opted-top': '{line-rule}',
    'opted-top-focused': '{line-strong}',
    'opted-ink': '{color.bp-ink}',
    'opted-fs': '{fs.table}',
    'opted-lh': '{lh.list}',
    'opted-line-gap': 2,
    'opted-small-fs': '{fs.body-sm}',
    'opted-strong-fw': '{fw.strong}',
    'opted-title-fs': '{fs.h3}',
    'opted-title-lh': '{lh.snug}',
    'opted-muted': '{color.bp-muted}',
    'opted-body-fw': '{fw.body}',
    'opted-rule': '{line-rule}',
    'opted-row-pad': '{space.3}',
    'opted-divider': '{hair}',
    'opted-part-indent': '{space.6}',
    'opted-num-gap': '{space.2}',
    'opted-num-w': 96,
    'opted-qty-w': 56,
    'opted-num-h': '{control-h-sm}',
    'opted-num-pad': '{space.2}',
    'opted-num-line': '{hair}',
    'opted-num-off': '{w.hair} solid transparent',
    'opted-radius': '{radius.r-hair}',
    'opted-bg': '{color.bp-surface}',
    'opted-focus': '{focus-line}',
    'opted-focus-offset': '{focus-offset}',
    'opted-figure-fw': '{fw.figure}',
    'opted-acts-gap': '{space.5}',
    'opted-bad': '{color.bp-signal}',
    'opted-col-min': 240,
    'opted-col-gap': '{space.9}',
    'opted-row-gap': '{space.9}',
    // the price book (DEC-019): a line's picture, what opens under it (Swap), and what usually goes with it
    'opted-below-gap': '{space.3}',
    'opted-suggest-gap': '{space.3}',
    // SignatureField: a line to sign on
    'sign-gap': '{space.3}',
    'sign-ink': '{color.bp-ink}',
    'sign-fs': '{fs.table}',
    'sign-lh': '{lh.list}',
    'sign-strong-fw': '{fw.strong}',
    'sign-h': 120,
    'sign-line': '{line-strong}',
    'sign-line-bad': '{w.strong} solid {color.bp-signal}',
    'sign-bg': '{color.bp-surface}',
    'sign-stroke': '{w.strong}',
    'sign-focus': '{focus-line}',
    'sign-focus-offset': '{focus-offset}',
    'sign-script-pad': '{space.8}',
    'sign-script-fs': '{fs.figure}',
    'sign-small-fs': '{fs.body-sm}',
    'sign-muted': '{color.bp-muted}',
    'sign-acts-gap': '{space.5}',
    'sign-bad': '{color.bp-signal}',
    // EntryDetail: anything on the record opened in full (one pattern, per-kind content)
    'entry-gap': '{space.6}',
    'entry-ink': '{color.bp-ink}',
    'entry-fs': '{fs.table}',
    'entry-lh': '{lh.list}',
    'entry-section-gap': '{space.3}',
    'entry-section-pad': '{space.5}',
    'entry-rule': '{hair}',
    'entry-muted': '{color.bp-muted}',
    'entry-heading-fs': '{fs.label}',
    'entry-heading-fw': '{fw.strong}',
    'entry-quote-pad': '{space.5}',
    'entry-quote-mark': '{w.focus} solid {color.bp-rule}',
    'entry-quote-fs': '{fs.body}',
    'entry-photo-gap': '{space.3}',
    'entry-steps-fs': '{fs.table}',
    // inline (History, opened in place): its sections, then its links in a row, the first the next step
    'entry-links-gap': '{space.3} {space.6}',
    'entry-bad': '{color.bp-signal}',
    // MiniInvoice: an invoice in short, where a History entry opens in place (Amir and Brook, 26 Oct)
    'miniinv-gap': '{space.3}',
    'miniinv-fs': '{fs.table}',
    'miniinv-ink': '{color.bp-ink}',
    'miniinv-muted': '{color.bp-muted}',
    'miniinv-bad': '{color.bp-signal}',
    'miniinv-number-fw': '{fw.strong}',
    'miniinv-head-gap': '{space.4}',
    // JobRecord: the job page, round 2
    'jobrec-sheet-pad': '{jobpage-sheet-pad}',
    'jobrec-sheet-pad-phone': '{jobpage-sheet-pad-phone}',
    'jobrec-sheet-line': '{line-rule}',
    'jobrec-sheet-bg': '{color.bp-surface}',
    'jobrec-ink': '{color.bp-ink}',
    'jobrec-muted': '{color.bp-muted}',
    'jobrec-bad': '{color.bp-signal}',
    'jobrec-fs': '{fs.table}',
    'jobrec-pad': '{space.4}',
    'jobrec-rule': '{hair}',
    'jobrec-strong-fw': '{fw.strong}',
    'jobrec-files-gap': '{space.5}',
    'jobrec-subhead-gap': '{space.2}',
    'jobrec-subhead-fs': '{photos-title-fs}',
    'jobrec-subhead-lh': '{photos-title-lh}',
    'jobrec-skel-gap': '{space.3}',
    // EstimateBuilder: an estimate built, presented and signed from the job (concept)
    'estbuild-gap': '{space.8}',
    'estbuild-pad': '{space.8}',
    'estbuild-pad-phone': '{space.6}',
    'estbuild-sheet-line': '{line-rule}',
    'estbuild-bg': '{color.bp-surface}',
    'estbuild-ink': '{color.bp-ink}',
    'estbuild-fs': '{fs.table}',
    'estbuild-lh': '{lh.list}',
    'estbuild-top-gap': '{space.6}',
    'estbuild-top-pad': '{space.5}',
    'estbuild-rule': '{hair}',
    'estbuild-crumb-gap': '{space.2}',
    'estbuild-muted': '{color.bp-muted}',
    'estbuild-small-fs': '{fs.label}',
    'estbuild-steps-gap': '{space.6}',
    'estbuild-step-pad': '{space.2} 0',
    'estbuild-step-line': '{w.mark} solid transparent',
    'estbuild-step-on': '{mark}',
    'estbuild-strong-fw': '{fw.strong}',
    'estbuild-focus': '{focus-line}',
    'estbuild-focus-offset': '{focus-offset}',
    'estbuild-line-gap': '{space.2}',
    'estbuild-title-fs': '{fs.h1}',
    'estbuild-title-lh': '{lh.tight}',
    'estbuild-bad': '{color.bp-signal}',
    'estbuild-field-min': 260,
    'estbuild-field-gap': '{space.6}',
    'estbuild-fields-max': 1100,
    'estbuild-book-w': 360,
    'estbuild-work-gap': '{space.8}',
    'estbuild-foot-gap': '{space.6}',
    'estbuild-h2-fs': '{fs.h3}',
    // the price book (DEC-019): Start from, above the price book in the side column: the job type's sets,
    // jobs like this, this address, a blank option; each a row with up to three pictures
    'estbuild-start-gap': '{space.2}',
    'estbuild-start-pad': '{space.4}',
    'estbuild-start-rule': '{hair}',
    'estbuild-start-on': '{mark}',
    'estbuild-start-off': '{w.mark} solid transparent',
    'estbuild-start-hover': '{color.bp-grid}',
    'estbuild-start-pics-gap': '{space.1}',
    'estbuild-start-name-fs': '{fs.body}',
    'estbuild-start-note-fs': '{fs.body-sm}',

    // ── calls (DESIGN.md §9) ──
    // CallStrip: the calls under the top bar, or a list in Inbox › Calls; one grid its rows line up in
    'callstrip-bg': '{color.bp-surface}',
    'callstrip-ink': '{color.bp-ink}',
    'callstrip-muted': '{color.bp-muted}',
    'callstrip-rule': '{line-rule}',
    'callstrip-list-rule': '{line-rule}',
    'callstrip-mark-col': 20,
    'callstrip-gutter': '{space.6}',
    'callstrip-name-min': 150,
    'callstrip-col-gap': '{space.6}',
    'callstrip-sr-size': 1,
    'callstrip-head-fs': '{fs.body}',
    'callstrip-head-fw': '{fw.strong}',
    'callstrip-head-lh': '{lh.snug}',
    'callstrip-head-gap': '{space.3}',
    'callstrip-head-pad': '{space.3}',
    'callstrip-count-fw': '{fw.body}',
    'callstrip-foot-fs': '{fs.table}',
    // CallRow: one call; ringing in the interrupt's full colour, your line in its wash
    'callrow-pad-y': '{space.4}',
    'callrow-pad-x': '{space.6}',
    'callrow-rule': '{hair}',
    'callrow-bg': '{color.bp-surface}',
    'callrow-ink': '{color.bp-ink}',
    'callrow-muted': '{color.bp-muted}',
    'callrow-bad': '{color.bp-signal}',
    'callrow-fs': '{fs.table}',
    'callrow-lh': '{lh.snug}',
    'callrow-state-fs': '{fs.label}',
    'callrow-state-fw': '{fw.strong}',
    'callrow-name-fs': '{fs.body-lg}',
    'callrow-name-fw': '{fw.strong}',
    'callrow-mark': 14,
    'callrow-mark-col': 20,
    'callrow-mark-line': '{line-rule}',
    'callrow-mark-dash': '{dash}',
    'callrow-mark-fill': '{color.bp-ink}',
    'callrow-mark-hatch': '{hatch}',
    'callrow-mark-bad': '{line-signal}',
    'callrow-mark-radius': '{radius.r-hair}',
    'callrow-ring-bg': '{interrupt-fill}',
    'callrow-ring-ink': '{interrupt-ink}',
    'callrow-quiet-bg': '{interrupt-quiet}',
    'callrow-ring-mark-line': '{w.rule} solid {color.bp-on-interrupt}',
    'callrow-ring-pulse-line': '{w.focus} solid {color.bp-on-interrupt}',
    'callrow-ring-act-border': '{w.rule} solid {color.bp-on-interrupt}',
    'callrow-ring-focus': '{w.focus} solid {color.bp-on-interrupt}',
    'callrow-ring-time': '{motion-ring}',
    'callrow-settle-time': '{motion-settle}',
    'callrow-curve': '{motion-curve}',
    'callrow-acts-gap': '{space.5}',
    'callrow-act-h': '{control-h}',
    'callrow-act-h-touch': '{control-h-touch}',
    'callrow-act-pad-x': '{control-pad-x}',
    'callrow-act-fs': '{control-fs}',
    'callrow-act-fw': '{control-fw}',
    'callrow-link-fw': '{fw.strong}',
    'callrow-act-border': '{line-rule}',
    'callrow-act-radius': '{radius.r-hair}',
    'callrow-act-bg': '{color.bp-surface}',
    'callrow-pri-bg': '{color.bp-ink}',
    'callrow-pri-ink': '{color.bp-on-ink}',
    'callrow-underline': '{w.hair}',
    'callrow-underline-offset': '{space.1}',
    'callrow-focus': '{focus-line}',
    'callrow-focus-offset': '{focus-offset}',
    'callrow-stack-gap': '{space.2}',
    'callrow-stack-mark-top': '{space.1}',
    // CallStatus: who is signed in, and whether calls ring to them
    'callstatus-h': '{control-h}',
    'callstatus-pad-x': '{space.4}',
    'callstatus-gap': '{space.3}',
    'callstatus-border': '{hair}',
    'callstatus-radius': '{radius.r-hair}',
    'callstatus-bg': '{color.bp-surface}',
    'callstatus-quiet-bg': '{interrupt-quiet}',
    'callstatus-ink': '{color.bp-ink}',
    'callstatus-muted': '{color.bp-muted}',
    'callstatus-fs': '{fs.label}',
    'callstatus-lh': '{lh.tight}',
    'callstatus-what-fw': '{fw.strong}',
    'callstatus-means-fs': '{fs.caption}',
    'callstatus-me': 26,
    'callstatus-me-radius': '{radius.r-round}',
    'callstatus-me-bg': '{color.bp-ink}',
    'callstatus-me-ink': '{color.bp-on-ink}',
    'callstatus-me-fs': '{fs.caption}',
    'callstatus-me-fw': '{fw.strong}',
    'callstatus-mark': 10,
    'callstatus-mark-line': '{line-rule}',
    'callstatus-mark-dash': '{dash}',
    'callstatus-mark-fill': '{color.bp-ink}',
    'callstatus-mark-radius': '{radius.r-hair}',
    'callstatus-underline': '{w.hair}',
    'callstatus-underline-offset': '{space.1}',
    'callstatus-focus': '{focus-line}',
    'callstatus-focus-offset': '{focus-offset}',
    // CallPage: the call's own page; the booking, the transcript beside it in view
    'callpage-pad': '{pad-sheet}',
    'callpage-pad-phone': '{space.6} {space.6} {space.9}',
    'callpage-bg': '{color.bp-surface}',
    'callpage-ink': '{color.bp-ink}',
    'callpage-muted': '{color.bp-muted}',
    'callpage-rule': '{line-rule}',
    'callpage-side-rule': '{hair}',
    'callpage-crumb-fs': '{fs.label}',
    'callpage-crumb-gap': '{space.3}',
    'callpage-head-pad': '{space.6}',
    'callpage-title-fs': '{fs.h1}',
    'callpage-title-fw': '{fw.strong}',
    'callpage-title-lh': '{lh.tight}',
    'callpage-sub-gap': '{space.2}',
    'callpage-sub-fs': '{fs.body-lg}',
    'callpage-sub-lh': '{lh.snug}',
    'callpage-gap': '{space.8}',
    'callpage-col-gap': '{space.8}',
    'callpage-main-gap': '{space.8}',
    'callpage-side-min': 300,
    'callpage-side-top': '{space.6}',
    'callpage-side-max-h': '86vh',
    'callpage-settle-time': '{motion-settle}',
    'callpage-curve': '{motion-curve}',
    'callpage-enter-y': '{space.3}',
    // SuggestionField: a booking field, and beside it what the call suggested for it
    'suggest-beside-w': 260,
    'suggest-gap': '{space.6}',
    'suggest-pad-y': '{space.3}',
    'suggest-top': '{space.7}',
    'suggest-inner-gap': '{space.1}',
    'suggest-pad': '{space.3} {space.4}',
    'suggest-dash': '{dash}',
    'suggest-radius': '{radius.r-hair}',
    'suggest-shown-bg': '{color.bp-grid}',
    'suggest-ink': '{color.bp-ink}',
    'suggest-muted': '{color.bp-muted}',
    'suggest-fs': '{fs.body-sm}',
    'suggest-lh': '{lh.list}',
    'suggest-kind-fs': '{fs.caption}',
    'suggest-kind-fw': '{fw.strong}',
    'suggest-value-fw': '{fw.strong}',
    'suggest-acts-gap': '{space.5}',
    'suggest-act-fw': '{fw.strong}',
    'suggest-act-h-touch': '{control-h-touch}',
    'suggest-underline': '{w.hair}',
    'suggest-underline-offset': '{space.1}',
    'suggest-focus': '{focus-line}',
    'suggest-focus-offset': '{focus-offset}',
    // LiveTranscript: who said what, a suggestion's words underlined
    'transcript-ink': '{color.bp-ink}',
    'transcript-muted': '{color.bp-muted}',
    'transcript-fs': '{fs.table}',
    'transcript-lh': '{lh.list}',
    'transcript-small-fs': '{fs.caption}',
    'transcript-who-fs': '{fs.label}',
    'transcript-who-fw': '{fw.strong}',
    'transcript-pad': '{space.3}',
    'transcript-state-gap': '{space.3}',
    'transcript-col-gap': '{space.4}',
    'transcript-words-gap': '{space.1}',
    'transcript-rule': '{line-rule}',
    'transcript-hair': '{hair}',
    'transcript-partial-rule': '{dash}',
    'transcript-mark': 12,
    'transcript-mark-line': '{line-rule}',
    'transcript-mark-dash': '{dash}',
    'transcript-mark-fill': '{color.bp-ink}',
    'transcript-mark-hatch': '{hatch}',
    'transcript-mark-radius': '{radius.r-hair}',
    'transcript-mark-under': '{w.rule}',
    'transcript-mark-offset': '{space.1}',
    'transcript-under': '{w.hair}',
    'transcript-shown-bg': '{color.bp-grid}',
    'transcript-touch': '{control-h-touch}',
    'transcript-focus': '{focus-line}',
    'transcript-focus-offset': '{focus-offset}',
    // VisitBooker: a morning or an afternoon with what's open, then the arrival window
    'vbook-gap': '{space.5}',
    'vbook-label-gap': '{space.2}',
    'vbook-ink': '{color.bp-ink}',
    'vbook-muted': '{color.bp-muted}',
    'vbook-fs': '{fs.table}',
    'vbook-lh': '{lh.tight}',
    'vbook-small-fs': '{fs.caption}',
    'vbook-label-fs': '{fs.label}',
    'vbook-label-fw': '{fw.strong}',
    'vbook-day-w': 64,
    'vbook-day-fw': '{fw.strong}',
    'vbook-slot-w': 140,
    'vbook-slot-gap': '{space.3}',
    'vbook-slot-h': '{control-h}',
    'vbook-slot-h-touch': '{control-h-touch}',
    'vbook-slot-pad-x': '{space.4}',
    'vbook-slot-pad-y': '{space.2}',
    'vbook-slot-border': '{hair}',
    'vbook-slot-hover': '{line-rule}',
    'vbook-full-border': '{w.hair} dashed {color.bp-rule}',
    'vbook-radius': '{radius.r-hair}',
    'vbook-bg': '{color.bp-surface}',
    'vbook-on-bg': '{color.bp-ink}',
    'vbook-on-ink': '{color.bp-on-ink}',
    'vbook-focus': '{focus-line}',
    'vbook-focus-offset': '{focus-offset}',
    // CallReason: after the call, why they called, to confirm
    'reason-gap': '{space.3}',
    'reason-ink': '{color.bp-ink}',
    'reason-muted': '{color.bp-muted}',
    'reason-fs': '{fs.body}',
    'reason-lh': '{lh.body}',
    'reason-small-fs': '{fs.body-sm}',
    'reason-state-gap': '{space.3}',
    'reason-mark': 12,
    'reason-mark-line': '{line-rule}',
    'reason-mark-hatch': '{hatch}',
    'reason-mark-radius': '{radius.r-hair}',
    'reason-h-top': '{space.4}',
    'reason-h-fs': '{fs.h2}',
    'reason-h-fw': '{fw.strong}',
    'reason-h-lh': '{lh.tight}',
    'reason-what-fs': '{fs.body-lg}',
    'reason-acts-gap': '{space.6}',
    'reason-acts-top': '{space.3}',
    // CallBooking: the booking made on a call, its five sections and the foot in view
    'callbook-ink': '{color.bp-ink}',
    'callbook-muted': '{color.bp-muted}',
    'callbook-bg': '{color.bp-surface}',
    'callbook-rule': '{line-rule}',
    'callbook-sec-pad': '{space.7}',
    'callbook-h-gap': '{space.3}',
    'callbook-h-fs': '{fs.h3}',
    'callbook-h-fw': '{fw.strong}',
    'callbook-h-lh': '{lh.tight}',
    'callbook-row-pad': '{space.3}',
    'callbook-two-gap': '{space.5}',
    'callbook-label-gap': '{space.2}',
    'callbook-label-fs': '{fs.label}',
    'callbook-label-fw': '{fw.strong}',
    'callbook-hint-fs': '{fs.body-sm}',
    'callbook-foot-gap': '{space.6}',
    'callbook-foot-pad': '{space.5}',
    'callbook-acts-gap': '{space.4}',
    // CallsInbox: Inbox › Calls, its lists in two columns
    'callsinbox-gap': '{space.9}',
    'callsinbox-col-gap': '{space.10}',
    'callsinbox-ink': '{color.bp-ink}',
    'callsinbox-muted': '{color.bp-muted}',
    'callsinbox-title-fs': '{fs.h1}',
    'callsinbox-title-fw': '{fw.strong}',
    'callsinbox-title-lh': '{lh.tight}',
    'callsinbox-sub-fs': '{fs.body-lg}',
    'callsinbox-sub-gap': '{space.2}',
    'callsinbox-head-pad': '{space.6}',
    'callsinbox-rule': '{line-rule}',
    'callsinbox-pad': '{pad-sheet}',
    'callsinbox-pad-phone': '{space.6} {space.6} {space.9}',
    'callsinbox-bg': '{color.bp-surface}',
    // the office app's shared stylesheets (office.ts), the same object as Riverstone's
    ...OFFICE_COMPONENTS,

    // ── Page patterns (packages/ui-web/src/patterns, BL-189): the skeletons every Blueprint page is
    // built from, each value the one today's approved pages draw with (blueprint.css, list-pages.css,
    // setup.css, setup-settings.css; pages/ux-full kit.css). ──
    // PageFrame: the top bar, the place's rail and the sheet on the paper grid (blueprint/Frame.tsx)
    'pframe-bg': '{color.bp-bg}',
    'pframe-surface': '{color.bp-surface}',
    'pframe-ink': '{color.bp-ink}',
    'pframe-muted': '{color.bp-muted}',
    'pframe-hot': '{color.bp-signal}',
    'pframe-line': '{line-rule}',
    'pframe-fs': '{fs.body}',
    // the app's body leading (styles.css body: 15px/1.45), which every office page inherits
    'pframe-lh': '1.45',
    'pframe-fw': '{fw.strong}',
    'pframe-focus': '{focus-line}',
    'pframe-focus-offset': '{focus-offset}',
    'pframe-top-h': 52,
    'pframe-min-h': '100vh',
    'pframe-brand-pad': '{space.7}',
    'pframe-brand-gap': '{space.3}',
    'pframe-brand-fs': '{fs.body-lg}',
    'pframe-brand-mark': 14,
    'pframe-place-pad': '{space.7}',
    'pframe-place-gap': '{space.2}',
    'pframe-mark-off': '{w.mark} solid transparent',
    'pframe-mark-on': '{mark}',
    'pframe-mark-pull': 'calc(-1 * {w.rule})',
    'pframe-cnt-fs': '{fs.caption}',
    'pframe-tool-pad': '{space.6}',
    'pframe-glyph': 18,
    'pframe-glyph-lg': 22,
    'pframe-glyph-stroke': '{icon-stroke}',
    'pframe-rail-w': 232,
    'pframe-rail-pad': '{space.7} 0 {space.8}',
    'pframe-rail-pad-x': '{space.7}',
    'pframe-rail-fs': '{fs.table}',
    'pframe-rail-head-gap': '{space.3}',
    'pframe-rail-head-pad': '{space.4}',
    'pframe-rail-head-fs': '{fs.h3}',
    'pframe-rail-head-lh': '{lh.tight}',
    'pframe-rail-grp-top': '{space.6}',
    'pframe-rail-grp-end': '{space.2}',
    'pframe-rail-grp-fs': '{fs.caption}',
    'pframe-rail-row-y': '{space.1}',
    'pframe-rail-gap': '{space.2}',
    'pframe-leader': '{w.hair} dotted {color.bp-rule}',
    'pframe-leader-min': '{space.5}',
    'pframe-leader-shift': 'calc(-1 * {space.1})',
    'pframe-hover-offset': 3,
    'pframe-current-thick': '{w.focus}',
    'pframe-current-offset': '{space.1}',
    'pframe-sheet-pad': '{pad-sheet}',
    'pframe-paper':
      'linear-gradient({color.bp-grid} {w.hair}, transparent {w.hair}), linear-gradient(90deg, {color.bp-grid} {w.hair}, transparent {w.hair})',
    'pframe-paper-size': '{space.6} {space.6}',
    'pframe-doc-pad': '{pad-sheet}',
    'pframe-phone-tool-pad': '{space.5}',
    'pframe-phone-tap': '{size.tap}',
    'pframe-phone-rail-pad': '{space.4} {space.5}',
    'pframe-phone-sheet-pad': '{space.4}',
    'pframe-phone-doc-pad-x': '{space.6}',
    // PageHead: a page's title block (DESIGN.md §3): crumb, h1, one muted line, at most two actions
    'pagehead-ink': '{color.bp-ink}',
    'pagehead-muted': '{color.bp-muted}',
    'pagehead-gap': '{space.5} {space.8}',
    'pagehead-end': '{space.2}',
    'pagehead-crumb-fs': '{fs.label}',
    'pagehead-crumb-gap': '{space.1}',
    'pagehead-crumb-offset': 2,
    'pagehead-title-fs': '{fs.h1}',
    'pagehead-title-fs-phone': '{fs.h2}',
    'pagehead-title-fw': '{fw.strong}',
    'pagehead-title-lh': '{lh.tight}',
    'pagehead-lede-fs': '{fs.body}',
    'pagehead-lede-gap': '{space.1}',
    'pagehead-lede-max': '75ch',
    'pagehead-acts-gap': '{space.3}',
    'pagehead-figs-top': '{space.8}',
    'pagehead-tabs-top': '{space.6}',
    'pagehead-tabs-rule': '{line-rule}',
    'pagehead-tab-pad': '{space.3} {space.6}',
    'pagehead-tab-fs': '{fs.body}',
    'pagehead-tab-fw': '{fw.strong}',
    'pagehead-tab-mark-off': '{w.mark} solid transparent',
    'pagehead-tab-mark-on': '{mark}',
    'pagehead-tab-pull': 'calc(-1 * {w.mark})',
    'pagehead-cnt-fs': '{fs.label}',
    'pagehead-focus': '{focus-line}',
    'pagehead-focus-offset': '{focus-offset}',
    // ListPage: views and filters, the list (about 60 %) and what goes with it, divided by a hairline
    'listpage-ink': '{color.bp-ink}',
    'listpage-muted': '{color.bp-muted}',
    'listpage-bad': '{color.bp-signal}',
    'listpage-top': '{space.8}',
    // under the head's figures: the sections' gap from the strip's rule (PageHead's own space.2 under it
    // collapses into it)
    'listpage-figs-top': '{space.9}',
    'listpage-filters-gap': '{space.3} {space.8}',
    'listpage-filters-end': '{space.8}',
    'listpage-views-gap': '{space.2} {space.6}',
    'listpage-view-pad': '{space.1} 0',
    'listpage-view-fs': '{fs.table}',
    'listpage-view-fw': '{fw.strong}',
    'listpage-view-thick': '{w.focus}',
    'listpage-view-offset': '{space.1}',
    'listpage-pick-gap': '{space.3}',
    'listpage-pick-radius': 0,
    // a search first in the filters (All customers): grows from a sentence's width to at most about two
    'listpage-search-w': 360,
    'listpage-search-max': 640,
    'listpage-cols-gap': '{space.8}',
    'listpage-divider': '{hair}',
    'listpage-stack-gap': '{space.9}',
    'listpage-sec-rule': '{line-rule}',
    'listpage-sec-pad': '{space.6}',
    'listpage-sec-gap': '{space.9}',
    'listpage-h-fs': '{fs.body-lg}',
    'listpage-h-fw': '{fw.strong}',
    'listpage-h-lh': '{lh.tight}',
    'listpage-h-gap': '{space.2}',
    'listpage-h-end': '{space.5}',
    'listpage-cnt-fs': '{fs.label}',
    'listpage-say-fs': '{fs.body}',
    'listpage-say-gap': '{space.3}',
    // a load still going after 10 s says so under its skeleton rows
    'listpage-slow-gap': '{space.3}',
    'listpage-focus': '{focus-line}',
    'listpage-focus-offset': '{focus-offset}',
    // the price book (DEC-019): a rail on the left (a category tree), the list beside it
    'listpage-rail-w': 184,
    'listpage-rail-gap': '{space.8}',
    // RecordPage: a record's head (JobHeader) and body (RecordLayout), and the page before it has a record
    'recpage-ink': '{color.bp-ink}',
    'recpage-muted': '{color.bp-muted}',
    'recpage-bad': '{color.bp-signal}',
    'recpage-fs': '{fs.table}',
    'recpage-gap': '{space.3}',
    'recpage-pad': '{space.4}',
    // SettingsPage: sections of settings, each what it is and its help on the left, its control on the right
    'setpage-ink': '{color.bp-ink}',
    'setpage-muted': '{color.bp-muted}',
    'setpage-bad': '{color.bp-signal}',
    // Setup's title block sits on the first section's rule: the body takes back PageHead's end margin
    'setpage-top': 'calc(-1 * {space.2})',
    'setpage-lh': '{lh.body}',
    'setpage-sec-rule': '{line-rule}',
    'setpage-sec-pad': '{space.6}',
    'setpage-sec-gap': '{space.9}',
    'setpage-h-fs': '{fs.body-lg}',
    'setpage-h-fw': '{fw.strong}',
    'setpage-h-lh': '{lh.tight}',
    'setpage-h-end': '{space.5}',
    'setpage-help-fs': '{fs.body-sm}',
    'setpage-line-fs': '{fs.body}',
    'setpage-line-pull': 0,
    'setpage-line-end': '{space.2}',
    // the read-only note under a section's line: space.5 below it (the margins collapse with the line's)
    'setpage-note-gap': '{space.5}',
    'setpage-line-gap': '{space.5}',
    'setpage-stack': '{space.5}',
    'setpage-row-pad': '{space.4}',
    'setpage-row-gap': '{space.2} {space.7}',
    'setpage-row-rule': '{hair}',
    'setpage-name-fs': '{fs.body-lg}',
    'setpage-name-fw': '{fw.strong}',
    'setpage-name-lh': '{lh.body}',
    'setpage-row-help-gap': '{space.1}',
    // a control's parts in a row (a number and its unit, a time to a time) as Setup's .row spaces them
    'setpage-ctl-gap': '{space.4} {space.7}',
    // a number's box (minutes, a share): as wide as Setup's number fields
    'setpage-short-w': 110,
    // where a value comes from (setup-settings.css .bp-src): caption size, muted, "Changed" in ink
    'setpage-src-fs': '{fs.caption}',
    'setpage-src-fw': '{fw.strong}',
    'setpage-src-gap': '{space.1} {space.4}',
    'setpage-state-fs': '{fs.label}',
    'setpage-state-fw': '{fw.strong}',
    // a setting under the one it depends on, the one a link opened, a value only read, a question first
    'setpage-sub-indent': '{space.7}',
    'setpage-sub-pad': '{space.5}',
    'setpage-sub-indent-phone': '{space.3}',
    'setpage-sub-pad-phone': '{space.4}',
    'setpage-found': '{mark}',
    'setpage-found-offset': '{space.2}',
    'setpage-tag-gap': '{space.2}',
    'setpage-val-fw': '{fw.strong}',
    'setpage-ask-gap': '{space.2} {space.4}',
    'setpage-fold-pad': '{space.2}',
    // the fold's one line at the font's own leading, as Setup's summary draws it
    'setpage-fold-lh': 'normal',
    // a setting's several fields (consumer financing): 8 apart, a plan's three sharing a line, 112 wide or more
    'setpage-fields-gap': '{space.3}',
    'setpage-field-min': 112,
    // FormSheet: a few fields beside the page, on the panel layer: title, body, the foot in view
    'formsheet-ink': '{color.bp-ink}',
    'formsheet-muted': '{color.bp-muted}',
    'formsheet-bad': '{color.bp-signal}',
    'formsheet-bg': '{color.bp-surface}',
    'formsheet-title-fs': '{fs.h3}',
    'formsheet-title-fw': '{fw.strong}',
    'formsheet-title-lh': '{lh.tight}',
    'formsheet-line-fs': '{fs.body}',
    'formsheet-gap': '{space.5}',
    // Layer keeps its first line clear of Close; here that's the title alone, not the whole form
    'formsheet-room': '{layer-close-room}',
    'formsheet-unroom': 'calc(-1 * {layer-close-room})',
    // the window's height less the panel's padding above and below (--sheet-pad)
    'formsheet-fill': 'calc(100vh - 2 * {space.7})',
    'formsheet-foot-rule': '{hair}',
    'formsheet-foot-pad': '{space.5}',
    'formsheet-foot-gap': '{space.3}',
    'formsheet-note-fs': '{fs.body-sm}',
    // ── Customers (BL-189: the place moved onto the patterns) ──
    // EquipmentList: every piece of equipment, one row each on hairlines (base.css's .flw-rows, as it drew them)
    'eqlist-ink': '{color.bp-ink}',
    'eqlist-muted': '{color.bp-muted}',
    'eqlist-bad': '{color.bp-signal}',
    'eqlist-fs': '{fs.body}',
    'eqlist-lh': '{lh.body}',
    'eqlist-row-gap': '{space.1} {space.5}',
    'eqlist-row-pad': '{pad-row} 0',
    'eqlist-rule': '{hair}',
    'eqlist-open-fs': '{fs.body-lg}',
    'eqlist-open-fw': '{fw.body}',
    'eqlist-underline-offset': 3,
    'eqlist-sub-fs': '{fs.body-sm}',
    'eqlist-state-fs': '{fs.label}',
    'eqlist-state-fw': '{fw.strong}',
    'eqlist-wait-offset': '{space.1}',
    'eqlist-more-top': '{space.4}',
    'eqlist-empty-rule': '{dash}',
    'eqlist-empty-pad': '{space.7} 0',
    'eqlist-focus': '{focus-line}',
    'eqlist-focus-offset': '{focus-offset}',
    // CustomerTable: All customers' answer, a head on the ink rule and rows on hairlines (bp-customers.css's table.bp-t)
    'custable-ink': '{color.bp-ink}',
    'custable-muted': '{color.bp-muted}',
    'custable-bad': '{color.bp-signal}',
    'custable-fs': '{fs.table}',
    'custable-th-pad': '{space.2} {space.3}',
    'custable-th-fs': '{fs.caption}',
    'custable-th-fw': '{fw.strong}',
    'custable-td-pad': '{space.3}',
    'custable-rule': '{line-rule}',
    'custable-hair': '{hair}',
    'custable-name-fs': '{fs.body}',
    'custable-name-fw': '{fw.strong}',
    'custable-meta-fs': '{fs.body-sm}',
    'custable-open-line': '{color.bp-rule}',
    'custable-open-offset': 3,
    'custable-owes-fw': '{fw.strong}',
    'custable-more-top': '{space.5}',
    'custable-focus': '{focus-line}',
    'custable-focus-offset': '{focus-offset}',
    // DuplicatePairList: the possible duplicates, a pair a hairline: what they share, then both as buttons
    'duppairs-ink': '{color.bp-ink}',
    'duppairs-muted': '{color.bp-muted}',
    'duppairs-fs': '{fs.body}',
    'duppairs-sub-fs': '{fs.body-sm}',
    'duppairs-note-end': '{space.3}',
    'duppairs-line-gap': '{space.2}',
    'duppairs-pad': '{pad-row} 0',
    'duppairs-rule': '{hair}',
    'duppairs-who-gap': '{space.2} {space.6}',
    'duppairs-num-fw': '{fw.body}',
    // NewCustomerFields: All customers' Add customer, its fields in columns of a readable width (new-customer.css)
    'newcust-ink': '{color.bp-ink}',
    'newcust-muted': '{color.bp-muted}',
    'newcust-bad': '{color.bp-signal}',
    'newcust-fs': '{fs.body}',
    'newcust-gap': '{space.5}',
    'newcust-max': 760,
    'newcust-col-w': 190,
    'newcust-field-gap': '{space.5}',
    'newcust-check-gap': '{space.2}',
    'newcust-label-fs': '{fs.label}',
    'newcust-label-fw': '{fw.strong}',
    'newcust-dupe-gap': '{space.4}',
    'newcust-dupe-pad': '{space.2} 0 {space.2} {space.5}',
    'newcust-dupe-mark': '{w.mark} dashed {color.bp-ink}',
    'newcust-acts-gap': '{space.3}',
    // ── Accounting (BL-189: the books' pages moved onto the patterns) ──
    // DataTable: records, one a row, to open; a head on the ink rule and rows on hairlines (base.css's
    // .flw-table, as Invoices and Payments and refunds drew them; BL-189 consolidation)
    'dtable-ink': '{color.bp-ink}',
    'dtable-muted': '{color.bp-muted}',
    'dtable-bad': '{color.bp-signal}',
    'dtable-lh': '{lh.body}',
    'dtable-fs': '{fs.table}',
    'dtable-th-pad': '{space.3} {space.4}',
    'dtable-th-fs': '{fs.caption}',
    'dtable-th-fw': '{fw.strong}',
    'dtable-td-pad': '{space.4}',
    'dtable-td-fw': '{fw.body}',
    'dtable-rule': '{line-rule}',
    'dtable-hair': '{hair}',
    'dtable-hover-bg': '{color.bp-grid}',
    'dtable-way-fw': '{fw.strong}',
    'dtable-way-offset': 3,
    'dtable-focus': '{focus-line}',
    'dtable-focus-offset': '{focus-offset}',
    'dtable-sub-fs': '{fs.body-sm}',
    'dtable-bad-fw': '{fw.strong}',
    'dtable-word-fs': '{fs.label}',
    'dtable-word-fw': '{fw.strong}',
    'dtable-wait-offset': '{space.1}',
    'dtable-more-top': '{space.4}',
    'dtable-empty-pad': '{space.7} 0',
    'dtable-empty-rule': '{dash}',
    'dtable-narrow-min': '34em',
    'dtable-foot-fw': '{fw.strong}',
    'dtable-per-min': '4em',
    'dtable-per-gap': '{space.3}',
    // stickyRowHeader: the row's head stays in view while the table scrolls sideways, on the sheet's
    // surface, over the cells it passes
    'dtable-sticky-bg': '{color.bp-surface}',
    'dtable-sticky-layer': '1',
    // the action column takes the width of its Link alone
    'dtable-action-w': '1%',
    'dtable-sr-size': 1,
    // FileField: a box that takes a file (Banking's statement), drawn as Field's box, on Field's values
    'filefield-h': '{input-h}',
    'filefield-pad-x': '{input-pad-x}',
    'filefield-gap': '{input-gap}',
    'filefield-border': '{input-border}',
    'filefield-radius': '{input-radius}',
    'filefield-bg': '{input-bg}',
    'filefield-ink': '{input-ink}',
    'filefield-placeholder-ink': '{input-placeholder-ink}',
    'filefield-fs': '{input-fs}',
    'filefield-lh': '{input-lh}',
    'filefield-label-fs': '{input-label-fs}',
    'filefield-label-fw': '{input-label-fw}',
    'filefield-label-lh': '{input-label-lh}',
    'filefield-label-ink': '{input-label-ink}',
    'filefield-help-fs': '{input-help-fs}',
    'filefield-help-lh': '{input-help-lh}',
    'filefield-help-ink': '{input-help-ink}',
    'filefield-error-ink': '{input-error-ink}',
    'filefield-error-border': '{input-error-border}',
    'filefield-error-fw': '{input-error-fw}',
    'filefield-disabled-border': '{input-disabled-border}',
    'filefield-disabled-ink': '{input-disabled-ink}',
    'filefield-focus': '{input-focus}',
    'filefield-focus-offset': '{input-focus-offset}',
    'filefield-choose-fw': '{fw.strong}',
    'filefield-choose-gap': '{space.4}',
    'filefield-choose-offset': '{space.1}',
    // CloseChecklist: a month's checks before it closes, a check a row on hairlines (base.css's .flw-ck)
    'closelist-ink': '{color.bp-ink}',
    'closelist-muted': '{color.bp-muted}',
    'closelist-bad': '{color.bp-signal}',
    'closelist-fs': '{fs.body}',
    'closelist-lh': '{lh.body}',
    'closelist-mark-col': '{space.6}',
    'closelist-row-gap': '{space.1} {space.5}',
    'closelist-row-pad': '{space.4} 0',
    'closelist-hair': '{hair}',
    'closelist-mark': '{space.5}',
    'closelist-mark-top': '{space.1}',
    'closelist-stroke': '{w.rule}',
    'closelist-dash': '2.5 2',
    'closelist-label-fw': '{fw.strong}',
    'closelist-sub-fs': '{fs.body-sm}',
    'closelist-note-top': '{space.5}',
    'closelist-done-end': '{space.3}',
    'closelist-done-fs': '{fs.body-lg}',
    'closelist-acts-gap': '{space.3} {space.6}',
    'closelist-acts-top': '{space.6}',
    'closelist-waits-pad': '{space.2} 0 {space.2} {space.5}',
    'closelist-dash-line': '{w.rule} dashed {color.bp-stroke}',
    'closelist-sr': 1,

    // ── the price book round (DEC-019, Amir 26 Oct: Concept A, with C's Start from, Swap and queue and B's
    // pictures). The prototype's accent is the tech app's (Fresh water); the office keeps Blueprint's
    // meaning: the chosen thing is ink (a mark or a fill), waiting is dashed, a problem red. ──
    // Picture: an item's house-style drawing, light or dark by the theme, or its state when there's none
    'pic-xs': 28,
    'pic-sm': 40,
    'pic-md': 56,
    'pic-lg': 120,
    'pic-hero': 240,
    'pic-fill-h': 176,
    'pic-tile': 76,
    'pic-ground': '{color.bp-bg}',
    'pic-radius': '{radius.r-hair}',
    'pic-line': '{hair}',
    'pic-none': '{dash}',
    'pic-word-fs': '{fs.caption}',
    'pic-word-lh': '{lh.snug}',
    'pic-word-ink': '{color.bp-muted}',
    'pic-word-pad': '{space.1}',
    'pic-sr': 1,
    // TreeList: systems › tasks with counts, at most three levels; the open one marked in ink
    'tree-ink': '{color.bp-ink}',
    'tree-muted': '{color.bp-muted}',
    'tree-fs': '{fs.table}',
    'tree-lh': '{lh.list}',
    'tree-fw': '{fw.body}',
    'tree-top-fw': '{fw.strong}',
    'tree-count-fs': '{fs.label}',
    'tree-pad': '{space.2} {space.4}',
    'tree-indent': '{space.5}',
    'tree-gap': '{space.3}',
    'tree-mark': '{mark}',
    'tree-mark-off': '{w.mark} solid transparent',
    'tree-on-bg': '{color.bp-grid}',
    'tree-hover-bg': '{color.bp-grid}',
    'tree-rule': '{hair}',
    'tree-foot-pad': '{space.4}',
    'tree-focus': '{focus-line}',
    'tree-focus-offset': 'calc(-1 * {focus-offset})',
    // PictureTable: a list to act on, one row an item with its picture; rows select and open
    'pictable-ink': '{color.bp-ink}',
    'pictable-muted': '{color.bp-muted}',
    'pictable-bad': '{color.bp-signal}',
    'pictable-fs': '{fs.table}',
    'pictable-lh': '{lh.list}',
    'pictable-head-fs': '{fs.caption}',
    'pictable-head-fw': '{fw.strong}',
    'pictable-head-rule': '{line-rule}',
    'pictable-rule': '{hair}',
    'pictable-pad-y': '{space.3}',
    'pictable-pad-x': '{space.4}',
    'pictable-name-fw': '{fw.strong}',
    'pictable-sub-fs': '{fs.body-sm}',
    'pictable-ck-w': 32,
    'pictable-pic-w': 56,
    'pictable-on-bg': '{color.bp-grid}',
    'pictable-on-mark': '{mark}',
    'pictable-mark-off': '{w.mark} solid transparent',
    'pictable-sr': 1,
    'pictable-hover-bg': '{color.bp-grid}',
    'pictable-wait-line': '{w.hair}',
    'pictable-wait-offset': '{space.1}',
    'pictable-underline': '{w.hair}',
    'pictable-underline-offset': 3,
    'pictable-stack-gap': '{space.1}',
    'pictable-focus': '{focus-line}',
    'pictable-focus-offset': '{focus-offset}',
    // PictureGrid: pictures as the thing you choose, one frame divided by hairlines, a + and an "on it" count
    'picgrid-min': 176,
    'picgrid-min-sm': 104,
    'picgrid-rule': '{hair}',
    'picgrid-frame': '{hair}',
    'picgrid-pad': '{space.4}',
    'picgrid-gap': '{space.2}',
    'picgrid-ink': '{color.bp-ink}',
    'picgrid-muted': '{color.bp-muted}',
    'picgrid-bad': '{color.bp-signal}',
    'picgrid-fs': '{fs.table}',
    'picgrid-lh': '{lh.snug}',
    'picgrid-name-fw': '{fw.strong}',
    'picgrid-price-fw': '{fw.figure}',
    'picgrid-sub-fs': '{fs.body-sm}',
    'picgrid-on': '{mark}',
    'picgrid-off': '{w.mark} solid transparent',
    'picgrid-hover-bg': '{color.bp-grid}',
    'picgrid-add': '{control-h}',
    'picgrid-add-line': '{line-rule}',
    'picgrid-add-bg': '{color.bp-surface}',
    'picgrid-add-hover': '{color.bp-grid}',
    'picgrid-glyph': '{size.icon}',
    'picgrid-stroke': '{icon-stroke}',
    'picgrid-inset': '{space.3}',
    'picgrid-focus': '{focus-line}',
    'picgrid-focus-offset': 'calc(-1 * {focus-offset})',
    // PictureChoice: one of several drawn as pictures (Swap), or pictures to add (what goes with it, dashed:
    // suggested, waiting on you)
    'picchoice-w': 112,
    'picchoice-gap': '{space.2}',
    'picchoice-pad': '{space.3}',
    'picchoice-line': '{hair}',
    'picchoice-on': '{mark}',
    'picchoice-hover-bg': '{color.bp-grid}',
    'picchoice-suggest': '{dash}',
    'picchoice-bg': '{color.bp-surface}',
    'picchoice-ink': '{color.bp-ink}',
    'picchoice-muted': '{color.bp-muted}',
    'picchoice-fs': '{fs.body-sm}',
    'picchoice-lh': '{lh.snug}',
    'picchoice-strong-fw': '{fw.strong}',
    'picchoice-label-fs': '{fs.label}',
    'picchoice-label-fw': '{fw.strong}',
    'picchoice-glyph': '{size.icon}',
    'picchoice-stroke': '{icon-stroke}',
    'picchoice-radius': '{radius.r-hair}',
    'picchoice-focus': '{focus-line}',
    'picchoice-focus-offset': '{focus-offset}',
    'picchoice-sr': 1,
    // ReviewQueue: one record at a time from a filtered list, its place in it, and its keys
    'queue-ink': '{color.bp-ink}',
    'queue-muted': '{color.bp-muted}',
    'queue-fs': '{fs.table}',
    'queue-lh': '{lh.list}',
    'queue-rule': '{line-rule}',
    'queue-hair': '{hair}',
    'queue-pad': '{space.6}',
    'queue-gap': '{space.5}',
    'queue-head-fw': '{fw.strong}',
    'queue-bar-w': 320,
    'queue-bar-h': 8,
    'queue-bar-line': '{hair}',
    'queue-bar-fill': '{color.bp-ink}',
    'queue-keys-fs': '{fs.body-sm}',
    'queue-say-fs': '{fs.body}',
    // the price book's own (packages/ui-web/src/pricebook): the item sheet, the asks, the page
    'pbsheet-ink': '{color.bp-ink}',
    'pbsheet-muted': '{color.bp-muted}',
    'pbsheet-bad': '{color.bp-signal}',
    'pbsheet-fs': '{fs.table}',
    'pbsheet-lh': '{lh.list}',
    'pbsheet-gap': '{space.6}',
    'pbsheet-sec-rule': '{hair}',
    'pbsheet-sec-pad': '{space.5}',
    'pbsheet-h-fs': '{fs.body}',
    'pbsheet-h-fw': '{fw.strong}',
    'pbsheet-line-gap': '{space.2}',
    'pbsheet-fields-gap': '{space.4}',
    'pbsheet-field-min': 140,
    'pbsheet-links-gap': '{space.2} {space.5}',
    'pbsheet-draft': '{wait-area}',
    'pbsheet-draft-bg': '{wait-area-bg}',
    'pbsheet-draft-pad': '{wait-area-pad}',
    'pbsheet-strong-fw': '{fw.strong}',
    'pbsheet-fw-body': '{fw.body}',
    'pbsheet-small-fs': '{fs.body-sm}',
    'pbsheet-label-fs': '{fs.label}',
    'pbsheet-row-pad': '{space.2}',
    'pbsheet-row-gap': '{space.4}',
    'pbsheet-glyph': '{size.icon}',
    'pbsheet-stroke': '{icon-stroke}',
    'asks-ink': '{color.bp-ink}',
    'asks-muted': '{color.bp-muted}',
    'asks-fs': '{fs.body-sm}',
    'asks-strong-fw': '{fw.strong}',
    'asks-wait-line': '{w.hair}',
    'asks-wait-offset': '{space.1}',
    'asks-gap': '{space.1} {space.4}',
    'pbpage-ink': '{color.bp-ink}',
    'pbpage-muted': '{color.bp-muted}',
    'pbpage-fs': '{fs.table}',
    'pbpage-gap': '{space.5}',
    'pbpage-tools-gap': '{space.4} {space.6}',
    'pbpage-find-w': 360,
    'pbpage-bulk-pad': '{space.3} {space.4}',
    'pbpage-bulk-bg': '{color.bp-grid}',
    'pbpage-bulk-rule': '{mark}',
    'pbpage-bulk-gap': '{space.3} {space.5}',
    'pbpage-strong-fw': '{fw.strong}',
    'pbpage-note-fs': '{fs.body-sm}',
    'pbpage-say-gap': '{space.3}',
    'pbpage-h-fs': '{fs.h3}',
    'pbpage-field-min': 180,
    // ── Truck stock, concept A (BL-203, 27 Oct): the office's Stock page (packages/ui-web, Domain/Stock) ──
    // Notice: what waits on someone (dashed, the waiting area) or what was just done (the ink mark)
    'notice-wait': '{wait-area}',
    'notice-wait-bg': '{wait-area-bg}',
    'notice-pad': '{wait-area-pad}',
    'notice-done-mark': '{mark}',
    'notice-done-pad': '{space.2} {space.6}',
    'notice-ink': '{color.bp-ink}',
    'notice-muted': '{color.bp-muted}',
    'notice-fs': '{fs.table}',
    'notice-lh': '{lh.list}',
    'notice-title-fs': '{fs.body}',
    'notice-title-fw': '{fw.strong}',
    'notice-gap': '{space.1}',
    'notice-acts-gap': '{space.3} {space.5}',
    'notice-acts-top': '{space.3}',
    'notice-focus': '{focus-line}',
    'notice-focus-offset': '{focus-offset}',
    // StockTable: a place's stock by category, or every place side by side; marks said in words
    'stocktable-ink': '{color.bp-ink}',
    'stocktable-muted': '{color.bp-muted}',
    'stocktable-strong-fw': '{fw.strong}',
    'stocktable-code-gap': '{space.3}',
    'stocktable-code-fs': '{fs.caption}',
    'stocktable-mark-gap': '{space.2}',
    'stocktable-mark-fs': '{fs.caption}',
    'stocktable-wait-line': '{w.hair}',
    'stocktable-wait-offset': '{space.1}',
    'stocktable-empty-pad': '{space.6}',
    'stocktable-empty-fs': '{fs.body}',
    // CountReview: a tech's count beside the books, a reason or a recount for each difference
    'countrev-gap': '{space.6}',
    'countrev-ink': '{color.bp-ink}',
    'countrev-muted': '{color.bp-muted}',
    'countrev-fs': '{fs.table}',
    'countrev-strong-fw': '{fw.strong}',
    'countrev-code-gap': '{space.3}',
    'countrev-code-fs': '{fs.caption}',
    'countrev-why-gap': '{space.3}',
    'countrev-why-min': 240,
    'countrev-field-w': 180,
    'countrev-wait-line': '{w.hair}',
    'countrev-wait-offset': '{space.1}',
    'countrev-bad-gap': '{space.1}',
    'countrev-note-fs': '{fs.body-sm}',
    'countrev-pair-min': 260,
    'countrev-line-gap': '{space.1}',
    'countrev-quote-pad': '{space.5}',
    'countrev-quote-rule': '{mark}',
    'countrev-label-fs': '{fs.label}',
    // RefillLines: a truck's next refill, each line with its why; trim, approve, remove, send
    'refill-gap': '{space.5}',
    'refill-ink': '{color.bp-ink}',
    'refill-muted': '{color.bp-muted}',
    'refill-fs': '{fs.table}',
    'refill-sub-fs': '{fs.body-sm}',
    'refill-strong-fw': '{fw.strong}',
    'refill-body-fw': '{fw.body}',
    'refill-line-gap': '{space.1}',
    'refill-qty-w': 120,
    'refill-unit-gap': '{space.2}',
    'refill-acts-gap': '{space.3}',
    'refill-foot-gap': '{space.5}',
    'refill-wait-line': '{w.hair}',
    'refill-wait-offset': '{space.1}',
    'refill-sr-size': 1,
    // RestockReceived: what didn't match on a restock, each with its choices and what they do
    'restock-gap': '{space.5}',
    'restock-ink': '{color.bp-ink}',
    'restock-muted': '{color.bp-muted}',
    'restock-fs': '{fs.table}',
    'restock-says-fs': '{fs.body-sm}',
    'restock-strong-fw': '{fw.strong}',
    'restock-line-gap': '{space.3}',
    'restock-issue-pad': '{space.4}',
    'restock-h-fs': '{fs.label}',
    'restock-acts-gap': '{space.3}',
    'restock-foot-gap': '{space.5}',
    'restock-wait-line': '{w.hair}',
    'restock-wait-offset': '{space.1}',
    // ReceiptFiling: a receipt's slip, its lines and where each goes, its sums, File it
    'receipt-gap': '{space.6}',
    'receipt-ink': '{color.bp-ink}',
    'receipt-muted': '{color.bp-muted}',
    'receipt-fs': '{fs.table}',
    'receipt-sub-fs': '{fs.body-sm}',
    'receipt-strong-fw': '{fw.strong}',
    'receipt-head-gap': '{space.6}',
    'receipt-slip-w': '{thumb-tile}',
    'receipt-line-gap': '{space.2}',
    'receipt-dest-w': 200,
    'receipt-find-w': 360,
    'receipt-result-pad': '{space.2}',
    'receipt-rule': '{hair}',
    'receipt-acts-gap': '{space.3}',
    'receipt-foot-gap': '{space.5}',
    // WorkList: work sent to you, a row each, opening its working view in place or beside the page
    'worklist-gap': '{space.3}',
    'worklist-ink': '{color.bp-ink}',
    'worklist-muted': '{color.bp-muted}',
    'worklist-head-rule': '{line-rule}',
    'worklist-head-pad': '{space.3}',
    'worklist-h-fs': '{fs.body}',
    'worklist-h-fw': '{fw.strong}',
    'worklist-count-fw': '{fw.body}',
    'worklist-body-gap': '{space.5}',
    'worklist-body-fs': '{fs.table}',
    // ── Schedule's Map (BL-189: the map's parts in their own folders, out of base.css) ──
    // DayMap: the day's visits on a grid, no street map; lengths in the drawing's units (1000 across)
    'daymap-ink': '{color.bp-ink}',
    'daymap-muted': '{color.bp-muted}',
    'daymap-bad': '{color.bp-signal}',
    'daymap-bg': '{color.bp-surface}',
    'daymap-grid': '{color.bp-grid}',
    'daymap-focus': '{color.bp-pen-1}',
    'daymap-pen': '{color.bp-signal}',
    ...pens('daymap'),
    'daymap-fs': '{fs.body}',
    'daymap-sub-fs': '{fs.body-sm}',
    'daymap-lh': '{lh.body}',
    'daymap-fw': '{fw.strong}',
    'daymap-body-fw': '{fw.body}',
    'daymap-area-fs': '{fs.h2}',
    'daymap-text-fs': '{fs.h3}',
    'daymap-min-w': 600,
    'daymap-grid-w': 1.5,
    'daymap-area-halo-w': 6,
    'daymap-text-halo-w': 5,
    'daymap-route-w': 4.5,
    'daymap-pin-w': 6,
    'daymap-on-w': 11,
    'daymap-focus-w': 12,
    'daymap-lead-w': 2,
    'daymap-scale-w': 3,
    'daymap-hair': '{hair}',
    'daymap-dash': '{dash}',
    'daymap-key-pad': '{space.3}',
    'daymap-none-pad': '{space.7} 0',
    // MapUnplaced: the visits with no point, under the map on a hairline (base.css's .flw-rows)
    'daymap-unplaced-top': '{space.7}',
    'daymap-unplaced-pad': '{space.5}',
    'daymap-unplaced-h-end': '{space.2}',
    'daymap-sub-gap': '{fs.body-sm}',
    'daymap-h-lh': '{lh.tight}',
    'daymap-row-gap': '{space.1} {space.5}',
    'daymap-row-pad': '{pad-row} 0',
    'daymap-on-pad': '{space.3}',
    'daymap-on-mark': '{mark}',
    // GoogleMap: Google's map or the stand-in, our drawing on top; the ground in map-style.ts's
    // MAP_COLOURS (land, street, label, halo; -dark- on a dark map); lengths in screen pixels
    'googlemap-land': '{color.bp-bg}',
    'googlemap-street': '{color.bp-surface}',
    'googlemap-label': '{color.bp-muted}',
    'googlemap-halo': '{color.bp-bg}',
    'googlemap-dark-land': '{color.bp-surface}',
    'googlemap-dark-street': '{color.bp-grid}',
    'googlemap-dark-label': '{color.bp-muted}',
    'googlemap-dark-halo': '{color.bp-surface}',
    'googlemap-ink': '{color.bp-ink}',
    'googlemap-muted': '{color.bp-muted}',
    'googlemap-bad': '{color.bp-signal}',
    'googlemap-surface': '{color.bp-surface}',
    'googlemap-focus': '{color.bp-pen-1}',
    'googlemap-pen': '{color.bp-signal}',
    ...pens('googlemap'),
    'googlemap-large-h': 420,
    'googlemap-small-h': 220,
    'googlemap-grid-w': 2,
    'googlemap-scale-w': 1.5,
    'googlemap-scale-fs': '{fs.caption}',
    'googlemap-route-edge-w': 7.5,
    'googlemap-route-w': 3.5,
    'googlemap-pin-w': 3.5,
    'googlemap-on-w': 6,
    'googlemap-focus-w': 7,
    'googlemap-text-fs': '{fs.label}',
    'googlemap-text-fw': '{fw.strong}',
    'googlemap-on-fw': '{fw.total}',
    'googlemap-text-halo-w': 4,
    'googlemap-shop-w': 2.5,
    'googlemap-me-w': 3,
    'googlemap-hair': '{hair}',
    'googlemap-key-pad': '{space.3}',
    'googlemap-key-fs': '{fs.body-sm}',
    'googlemap-lh': '{lh.body}',
    // VisitCard: the visit picked on the map (base.css's .flw-visit, .flw-facts, .flw-label, .flw-tech)
    'visitcard-ink': '{color.bp-ink}',
    'visitcard-muted': '{color.bp-muted}',
    'visitcard-bad': '{color.bp-signal}',
    'visitcard-fs': '{fs.body}',
    'visitcard-lh': '{lh.body}',
    'visitcard-fw': '{fw.strong}',
    'visitcard-head-gap': '{space.3}',
    'visitcard-head-end': '{space.1}',
    'visitcard-word-fs': '{fs.label}',
    'visitcard-wait-offset': '{space.1}',
    'visitcard-customer-fs': '{fs.name}',
    'visitcard-customer-lh': '{lh.tight}',
    'visitcard-address-gap': '{space.1} 0 {space.5}',
    'visitcard-sub-fs': '{fs.body-sm}',
    'visitcard-facts-gap': '0 {space.6}',
    'visitcard-facts-pad': '{space.2} 0',
    'visitcard-hair': '{hair}',
    'visitcard-techs-gap': '{space.1} {space.5}',
    'visitcard-note-gap': '{space.3}',
    'visitcard-empty-pad': '{space.7} 0',
    'visitcard-empty-top': '{fs.body}',
    'visitcard-dash': '{dash}',
    // ── Home's dashboards (BL-189): the DashboardPage pattern, and what Home draws on it ──
    // DashboardPage: the title block, then the work on the left (about 60 %) and what it needs on the
    // right behind a hairline, then the whole width under them (home.css's .bp-home-body, .bp-grid2)
    'dashpage-ink': '{color.bp-ink}',
    'dashpage-muted': '{color.bp-muted}',
    'dashpage-bad': '{color.bp-signal}',
    'dashpage-top': '{space.8}',
    'dashpage-figs-top': '{space.9}',
    'dashpage-bar-top': '{space.7}',
    'dashpage-strip-top': '{space.8}',
    'dashpage-cols-gap': '{space.8}',
    'dashpage-divider': '{hair}',
    'dashpage-stack-gap': '{space.9}',
    'dashpage-sec-rule': '{line-rule}',
    'dashpage-sec-pad': '{pad-section}',
    'dashpage-below-gap': '{gap-section}',
    'dashpage-say-fs': '{fs.body}',
    'dashpage-say-lh': '{lh.body}',
    'dashpage-say-gap': '{space.3}',
    'dashpage-slow-gap': '{space.3}',
    'dashpage-foot-fs': '{fs.body-sm}',
    'dashpage-foot-lh': '{lh.body}',
    'dashpage-foot-top': '{space.9}',
    // Label: a status word (base.css's .flw-label): muted, waiting dashed under, red only for a problem
    'label-fs': '{fs.label}',
    'label-fw': '{fw.strong}',
    'label-ink': '{color.bp-ink}',
    'label-muted': '{color.bp-muted}',
    'label-bad': '{color.bp-signal}',
    'label-wait-offset': '{space.1}',
    // Meter: a bar against a scale, a drawing that keeps its frame (base.css's .flw-meter, .flw-mark);
    // the hatch is base.css's lighter one (1 px every 6), not the meaning's
    'meter-h': 10,
    'meter-frame': '{line-rule}',
    'meter-pen': '{color.bp-ink}',
    ...pens('meter'),
    'meter-hatch': '{hatch}',
    'meter-low': '{color.bp-signal}',
    'meter-mark-out': 'calc(-1 * {space.1})',
    'meter-mark': '{line-rule}',
    'meter-floor': '{dash-signal}',
    // GoalPace: the month against the goal (gp.pace): its figure, the bar to the goal, the pace in words
    'pace-ink': '{color.bp-ink}',
    'pace-muted': '{color.bp-muted}',
    'pace-fs': '{fs.body}',
    'pace-lh': '{lh.body}',
    'pace-sub-fs': '{fs.body-sm}',
    'pace-meter-top': '{space.4}',
    'pace-line-top': '{space.3}',
    'pace-acts-gap-touch': '{space.3}',
    'pace-acts-top': '{space.6}',
    // Aging: who owes what (receivables.aging): its figure, each bucket's bar, the latest late invoices
    'aging-ink': '{color.bp-ink}',
    'aging-muted': '{color.bp-muted}',
    'aging-bad': '{color.bp-signal}',
    'aging-bad-fw': '{fw.strong}',
    'aging-fs': '{fs.body}',
    'aging-lh': '{lh.body}',
    'aging-sub-fs': '{fs.body-sm}',
    'aging-buckets-top': '{space.4}',
    'aging-row-gap': '{space.1} {space.5}',
    'aging-row-pad': '{pad-row} 0',
    'aging-rule': '{hair}',
    'aging-acts-gap-touch': '{space.3}',
    'aging-acts-top': '{space.6}',
  },
  motion: {
    durations: { instant: 0, fast: 120, normal: 160, slow: 240 },
    easings: { standard: [0.2, 0, 0, 1], enter: [0, 0, 0, 1], exit: [0.3, 0, 1, 1] },
  },
});
